import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ElderlyCare.css';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import { useAuth } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faHospital,
  faHome,
  faMoneyBill,
  faUsers,
  faShieldAlt,
  faHeadset,
  faAmbulance,
  faUserMd,
  faStethoscope,
  faFileMedical,
  faUtensils,
  faWheelchair,
  faHandHoldingHeart,
  faCar,
  faBriefcaseMedical,
  faLaptopMedical,
  faTruck,
  faTools,
  faHeartbeat,
  faCalendarCheck,
  faComments,
  faTheaterMasks,
  faHiking,
  faBriefcase,
  faIdCard,
  faLock,
  faRoad,
  faClock,
  faMedkit,
  faCapsules,
  faSmile,
  faChartLine,
  faLungs,
  faThermometerHalf,
  faTint,
  faBed,
  faWeight,
  faWalking,
  faBrain,
  faArrowUp,
  faArrowDown,
  faMinus,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faBell,
  faPills,
  faCalendarAlt,
  faVial,
  faClipboardCheck,
  faSync,
  faSyringe,
  // faWifi,  // unused since Remote Monitoring sub-tab was hidden
  faChartBar,
  // faUserFriends,  // unused since Remote Monitoring sub-tab was hidden
  // faRobot,  // unused since Digital Features sub-tab was hidden
  // faArrowRight,  // unused since Digital Features sub-tab was hidden
  faEdit,
  faUserPlus,
  faCheck,
  faCircleExclamation,
  faPenToSquare,
  faStopwatch,
  faMicrochip,
  faMobileScreen,
} from '@fortawesome/free-solid-svg-icons';
import {
  AreaChart,
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import { Container } from 'react-bootstrap';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import reminderService, { Reminder, ReminderPayload } from '../../services/reminderService';
import elderlyCareService, {
  ElderlyVitalPayload,
  ElderlyCareCategory,
} from '../../services/elderlyCareService';

/* ------------------------------------------------------------------ */
/*  FIXED TABS (all other tab content is admin-managed via API)       */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  VITAL METRICS DATA                                                */
/* ------------------------------------------------------------------ */

interface VitalMetric {
  id: string;
  name: string;
  icon: any;
  useCase: string;
  unit: string;
  normalRange: string;
  color: string;
}

const vitalMetrics: VitalMetric[] = [
  {
    id: 'heart_rate',
    name: 'Heart Rate & ECG',
    icon: faHeartbeat,
    useCase: 'Cardiac risk prediction & arrhythmia detection',
    unit: 'bpm',
    normalRange: '60-100',
    color: '#ef4444',
  },
  {
    id: 'blood_pressure',
    name: 'Blood Pressure',
    icon: faTint,
    useCase: 'Hypertension & stroke prevention',
    unit: 'mmHg',
    normalRange: '90-140/60-90',
    color: '#3b82f6',
  },
  {
    id: 'blood_sugar',
    name: 'Blood Sugar',
    icon: faTint,
    useCase: 'Diabetes control & diet optimization',
    unit: 'mg/dL',
    normalRange: '70-140',
    color: '#f59e0b',
  },
  {
    id: 'spo2',
    name: 'SpO2 & Respiratory Rate',
    icon: faLungs,
    useCase: 'COPD, asthma & infection monitoring',
    unit: '% / breaths/min',
    normalRange: '95-100% / 12-20',
    color: '#06b6d4',
  },
  {
    id: 'temperature',
    name: 'Body Temperature',
    icon: faThermometerHalf,
    useCase: 'Early fever alerts & infection escalation',
    unit: '\u00B0F',
    normalRange: '97.8-99.1',
    color: '#f97316',
  },
  {
    id: 'sleep',
    name: 'Sleep Patterns',
    icon: faBed,
    useCase: 'Sleep apnea & psychological wellness',
    unit: 'hours',
    normalRange: '6-9',
    color: '#8b5cf6',
  },
  {
    id: 'weight_bmi',
    name: 'Weight / BMI / Muscle Mass',
    icon: faWeight,
    useCase: 'Nutrition monitoring',
    unit: 'kg / BMI',
    normalRange: '18.5-24.9 BMI',
    color: '#10b981',
  },
  {
    id: 'mobility',
    name: 'Steps & Mobility',
    icon: faWalking,
    useCase: 'Fall risk assessment & rehabilitation progress',
    unit: 'steps',
    normalRange: '2000-10000',
    color: '#ec4899',
  },
  {
    id: 'cognitive',
    name: 'Cognitive Ability Tests',
    icon: faBrain,
    useCase: 'Dementia / Alzheimer early screening',
    unit: 'score',
    normalRange: '24-30 MMSE',
    color: '#6366f1',
  },
  {
    id: 'mood',
    name: 'Pain & Mood Logging',
    icon: faSmile,
    useCase: 'Psychological and palliative care context',
    unit: 'scale 1-10',
    normalRange: 'N/A',
    color: '#14b8a6',
  },
];

/* ------------------------------------------------------------------ */
/*  DIGITAL FEATURES                                                  */
/* ------------------------------------------------------------------ */

/* --- DISABLED: used only by the hidden Digital Features sub-tab.
const digitalFeatures = [
  { icon: faBell, title: 'Scheduled Vitals Tracking Reminders', description: 'Automated reminders for checking BP, SpO2, heart rate, temperature, etc. Users record vitals through the app or connected devices.', color: '#0e7490', link: '/elderly-care', action: 'reminders' },
  { icon: faSync, title: 'Remote Readings Auto-Synced from IoT/Wearables', description: 'Data from approved IoT devices/wearables is captured in real time and automatically synced without manual input.', color: '#8b5cf6', link: '/wearable-devices', action: 'wearables' },
  { icon: faChartBar, title: 'Health Trend Analytics & Anomaly Alerts', description: 'System analyzes incoming data to identify trends, deviations, and patterns. Abnormal readings trigger instant alerts to user and caregivers.', color: '#ef4444', link: '/health-records', action: 'analytics' },
  { icon: faUserFriends, title: 'Family-Facing Dashboard & Wellness Reports', description: 'Family members view shared dashboard with daily vitals, trends, goals, and alerts. Periodic reports summarize health progress.', color: '#f59e0b', link: '/health-summary', action: 'reports' },
  { icon: faRobot, title: 'AI-Powered Predictive Health Scoring', description: 'AI models assess combined vitals, lifestyle data, and historical patterns to generate predictive health scores for proactive care.', color: '#6366f1', link: '/disease-prediction', action: 'ai' },
];
*/



/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  TAB DEFINITIONS WITH ACCENT COLORS                                */
/* ------------------------------------------------------------------ */

const FIXED_TABS = [
  { id: 'vitals', label: 'Vital Metrics', icon: faChartLine, color: '#0e7490', lightColor: '#ecfeff', image: '' },
  { id: 'reminders', label: 'Reminders', icon: faBell, color: '#e11d48', lightColor: '#fff1f2', image: '' },
];

/* ------------------------------------------------------------------ */
/*  TAB SECTION HEADER DESCRIPTIONS                                   */
/* ------------------------------------------------------------------ */

const tabDescriptions: Record<string, { title: string; subtitle: string }> = {
  vitals: {
    title: 'Vital Metrics Tracking',
    subtitle: 'Monitor and track key health vitals for elderly care. Some metrics can be tracked via video-based AI detection while others require dedicated sensors.',
  },
  reminders: {
    title: 'Personal Health Reminders & Follow-Ups',
    subtitle: 'Comprehensive reminder system for medications, appointments, test results, vaccinations, insurance renewals, and preventive health check-ups.',
  },
};

/* ------------------------------------------------------------------ */
/*  DYNAMIC ADMIN-MANAGED CONTENT (categories + services)             */
/* ------------------------------------------------------------------ */

interface ServiceCardItem {
  id?: number;
  title: string;
  description: string;
  icon: any;
}

const ecIconMap: Record<string, any> = {
  ambulance: faAmbulance,
  bell: faBell,
  briefcase: faBriefcase,
  'briefcase-medical': faBriefcaseMedical,
  'calendar-check': faCalendarCheck,
  capsules: faCapsules,
  car: faCar,
  'chart-line': faChartLine,
  clock: faClock,
  comments: faComments,
  'file-medical': faFileMedical,
  'hand-holding-heart': faHandHoldingHeart,
  headset: faHeadset,
  hiking: faHiking,
  home: faHome,
  hospital: faHospital,
  'id-card': faIdCard,
  'laptop-medical': faLaptopMedical,
  lock: faLock,
  medkit: faMedkit,
  'money-bill': faMoneyBill,
  phone: faPhone,
  road: faRoad,
  'shield-alt': faShieldAlt,
  smile: faSmile,
  stethoscope: faStethoscope,
  'theater-masks': faTheaterMasks,
  tools: faTools,
  truck: faTruck,
  'user-md': faUserMd,
  users: faUsers,
  utensils: faUtensils,
  wheelchair: faWheelchair,
};

const resolveEcIcon = (key?: string): any => (key && ecIconMap[key]) || faStethoscope;

// Map frontend display IDs to backend metric_type values for the Trends tab.
// Combined rows (SpO2 & Respiratory Rate, Weight / BMI) fetch BOTH series so
// their charts, stats and combined latest value are complete.
const metricBackendKeys: Record<string, string[]> = {
  heart_rate: ['heart_rate'],
  blood_pressure: ['blood_pressure'],
  blood_sugar: ['blood_sugar'],
  spo2: ['spo2', 'respiratory_rate'],
  temperature: ['temperature'],
  sleep: ['sleep'],
  weight_bmi: ['weight', 'bmi'],
  mobility: ['steps'],
  cognitive: ['cognitive'],
  mood: ['mood'],
};

// One plotted line. `name` is a plain-language label (no clinical jargon),
// `band` is the healthy range drawn behind the line. Series that share a unit
// (systolic + diastolic) are plotted on one axis via `sharedAxis`.
interface TrendSeriesMeta {
  key: string;
  nameKey: string;
  unit: string;
  band?: [number, number];
  /** Draw on the same chart/axis as the preceding series (same unit only). */
  sharedAxis?: boolean;
  /** Use a darker/lighter tint of the parent metric color for this line. */
  tint?: 'full' | 'soft';
}

const metricChartMeta: Record<string, { series: TrendSeriesMeta[] }> = {
  heart_rate: { series: [{ key: 'heart_rate', nameKey: 'labelHeartRate', unit: 'bpm', band: [60, 100] }] },
  blood_pressure: {
    series: [
      { key: 'blood_pressure', nameKey: 'labelSystolic', unit: 'mmHg', band: [90, 140] },
      { key: 'blood_pressure', nameKey: 'labelDiastolic', unit: 'mmHg', band: [60, 90], sharedAxis: true, tint: 'soft' },
    ],
  },
  blood_sugar: { series: [{ key: 'blood_sugar', nameKey: 'labelBloodSugar', unit: 'mg/dL', band: [70, 140] }] },
  spo2: {
    series: [
      { key: 'spo2', nameKey: 'labelOxygen', unit: '%', band: [95, 100] },
      { key: 'respiratory_rate', nameKey: 'labelRespRate', unit: 'breaths/min', band: [12, 20] },
    ],
  },
  temperature: { series: [{ key: 'temperature', nameKey: 'labelTemperature', unit: '°F', band: [97.8, 99.1] }] },
  sleep: { series: [{ key: 'sleep', nameKey: 'labelSleep', unit: 'hours', band: [6, 9] }] },
  weight_bmi: {
    series: [
      { key: 'weight', nameKey: 'labelWeight', unit: 'kg' },
      { key: 'bmi', nameKey: 'labelBmi', unit: 'kg/m²', band: [18.5, 24.9] },
    ],
  },
  mobility: { series: [{ key: 'steps', nameKey: 'labelSteps', unit: 'steps', band: [2000, 10000] }] },
  cognitive: { series: [{ key: 'cognitive', nameKey: 'labelCognitive', unit: 'score', band: [24, 30] }] },
  mood: { series: [{ key: 'mood', nameKey: 'labelMood', unit: 'scale 1-10' }] },
};

// Which direction counts as a deterioration, so the trend indicator is colored
// by clinical meaning (not a blunt up=red / down=green).
const metricClinicDirection: Record<string, 'highBad' | 'lowBad' | 'neutral'> = {
  heart_rate: 'highBad',
  blood_pressure: 'highBad',
  blood_sugar: 'highBad',
  temperature: 'highBad',
  weight_bmi: 'highBad',
  mood: 'highBad',
  spo2: 'lowBad',
  mobility: 'lowBad',
  cognitive: 'lowBad',
  sleep: 'neutral',
};

/* ------------------------------------------------------------------ */
/*  TREND DATA PIPELINE                                                */
/* ------------------------------------------------------------------ */

interface TrendPoint {
  ts?: string;
  date: string;
  value: number;
  valueSecondary?: number | null;
}

type TrendSeriesByMetric = Record<string, Record<string, TrendPoint[]>>;

function toTrendPoints(items: any[]): TrendPoint[] {
  return (Array.isArray(items) ? items : []).map((item: any) => ({
    ts: item.recorded_at,
    date: new Date(item.recorded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    value: typeof item.value === 'number' ? item.value : Number(item.value) || 0,
    valueSecondary: item.value_secondary != null ? Number(item.value_secondary) : null,
  }));
}

// Format a trend value without trailing zeros: 75 -> "75", 71.5 -> "71.5".
const fmtNum = (n: number): string => {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

// Trim trailing ".0" from numeric tokens inside a string (e.g. older alert
// messages stored as "120.0") without touching real decimals like "109.8".
const cleanNum = (s: string): string =>
  (s || '').replace(/-?\b\d+(\.\d+)?\b/g, (tok) => {
    const n = Number(tok);
    return !Number.isNaN(n) && Number.isInteger(n) ? String(n) : tok;
  });

// Bucket raw readings into one point per day (daily mean) for readable long-range charts.
function bucketByDay(points: TrendPoint[]): TrendPoint[] {
  if (!points.length) return [];
  const buckets = new Map<string, { sum: number; sumSec: number; n: number; nSec: number; ts: string }>();
  points.forEach((p) => {
    if (p.ts == null) return;
    const dayKey = new Date(p.ts).toDateString();
    const b = buckets.get(dayKey) || { sum: 0, sumSec: 0, n: 0, nSec: 0, ts: p.ts };
    b.sum += p.value;
    b.n += 1;
    if (p.valueSecondary != null) {
      b.sumSec += p.valueSecondary;
      b.nSec += 1;
    }
    if (p.ts > b.ts) b.ts = p.ts;
    buckets.set(dayKey, b);
  });
  return Array.from(buckets.values())
    .map((b) => ({
      ts: b.ts,
      date: new Date(b.ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      value: parseFloat((b.sum / b.n).toFixed(1)),
      valueSecondary: b.nSec ? parseFloat((b.sumSec / b.nSec).toFixed(1)) : null,
    }))
    .sort((a, b) => (a.ts < b.ts ? -1 : 1));
}

// Chart bar with a `null` value for days that have no readings (gap).
type ChartPoint = {
  ts?: string;
  date: string;
  value: number | null;
  valueSecondary?: number | null;
};

// Expand bucketed points into one slot per calendar day between the first and
// last reading, inserting null gaps so bars are spaced by real time. This keeps
// the chart honest when data only exists for a few scattered days.
function fillDayGaps(points: TrendPoint[]): ChartPoint[] {
  if (!points.length) return [];
  const first = points[0].ts;
  const last = points[points.length - 1].ts;
  if (!first || !last) return points.map((p) => ({ ...p }));

  const byKey = new Map<string, TrendPoint>();
  points.forEach((p) => {
    if (p.ts) byKey.set(new Date(p.ts).toDateString(), p);
  });

  const out: ChartPoint[] = [];
  const cur = new Date(first);
  const end = new Date(last);
  while (cur <= end) {
    const key = cur.toDateString();
    const p = byKey.get(key);
    out.push(
      p
        ? { ts: p.ts, date: p.date, value: p.value, valueSecondary: p.valueSecondary }
        : { date: cur.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), value: null, valueSecondary: null },
    );
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  TRENDS TAB HELPERS — built for non-clinical readers               */
/* ------------------------------------------------------------------ */

type ReadingStatus = 'normal' | 'high' | 'low' | 'unknown';

const classifyReading = (value: number | null | undefined, band?: [number, number]): ReadingStatus => {
  if (value == null || Number.isNaN(value)) return 'unknown';
  if (!band) return 'unknown';
  if (value < band[0]) return 'low';
  if (value > band[1]) return 'high';
  return 'normal';
};

interface SeriesSummary {
  total: number;
  outOfRange: number;
  avg: number | null;
  low: number | null;
  high: number | null;
  latest: number | null;
  latestDate: string | null;
  status: 'normal' | 'watch' | 'alert' | 'none';
}

// Roll a list of readings into the few numbers a normal user actually needs,
// plus one plain status word that replaces clinical jargon.
function summarizeSeries(
  values: { value: number | null; date: string }[],
  band?: [number, number],
): SeriesSummary {
  const readings = values.filter((v) => v.value != null) as { value: number; date: string }[];
  if (!readings.length) {
    return { total: 0, outOfRange: 0, avg: null, low: null, high: null, latest: null, latestDate: null, status: 'none' };
  }
  const nums = readings.map((r) => r.value);
  const last = readings[readings.length - 1];
  const outOfRange = band ? nums.filter((v) => classifyReading(v, band) !== 'normal').length : 0;
  const latestStatus = band ? classifyReading(last.value, band) : 'unknown';
  const status: SeriesSummary['status'] = !band
    ? 'normal'
    : latestStatus === 'high' || latestStatus === 'low'
      ? 'alert'
      : outOfRange > 0
        ? 'watch'
        : 'normal';
  return {
    total: readings.length,
    outOfRange,
    avg: nums.reduce((s, v) => s + v, 0) / nums.length,
    low: Math.min(...nums),
    high: Math.max(...nums),
    latest: last.value,
    latestDate: last.date,
    status,
  };
}

// One row per calendar day across the whole window, with `null` where a day
// has no reading. Shared by every line in a chart group so the x-axis stays
// honest (gaps are visible) and lines line up.
type ChartRow = Record<string, number | string | null>;

// Long windows (30/90 days) turn into a noisy zig-zag. Collapse each run of
// consecutive days into its average so the shape stays readable.
function downsampleRows(rows: ChartRow[], keys: string[], maxPoints = 40): ChartRow[] {
  if (rows.length <= maxPoints) return rows;
  const groupSize = Math.ceil(rows.length / maxPoints);
  const out: ChartRow[] = [];
  for (let i = 0; i < rows.length; i += groupSize) {
    const group = rows.slice(i, i + groupSize);
    const row: ChartRow = { date: group[group.length - 1].date };
    keys.forEach((k) => {
      const vals = group.map((r) => r[k]).filter((v): v is number => typeof v === 'number');
      row[k] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    });
    out.push(row);
  }
  return out;
}

// Y-domain that always contains the readings and every healthy band, so the
// "usual range" shading is never squeezed off-screen.
function buildYDomain(values: number[], bands: [number, number][] = []): [number, number] {
  const pool = [...values];
  bands.forEach(([lo, hi]) => pool.push(lo, hi));
  if (!pool.length) return [0, 1];
  let min = Math.min(...pool);
  let max = Math.max(...pool);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.12;
  return [Math.floor((min - pad) * 10) / 10, Math.ceil((max + pad) * 10) / 10];
}

// Small help tooltip (title attr) for normal users next to medical terms.
const HelpTip = ({ text }: { text: string }) => (
  <span className="ec-helptip" title={text} style={{ cursor: 'help', marginLeft: 5, color: '#94a3b8' }}>
    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.72rem' }} />
  </span>
);

// Plain-language help for medical terms in the vitals table.
const VITAL_HELP: Record<string, string> = {
  blood_pressure: 'Force of blood against your artery walls — two numbers: systolic (during a heartbeat) over diastolic (between beats).',
  blood_sugar: 'Amount of sugar (glucose) in your blood. Keep in range to avoid energy or mood swings.',
  spo2: 'Oxygen level in the blood. 95-100% is healthy for most people.',
  respiratory_rate: 'Number of breaths per minute at rest. Normal is around 12-20.',
  temperature: 'Body temperature. Around 98-99°F is typical for most adults.',
  weight: 'Body weight in kilograms. Steady weight is a good sign of overall health.',
  bmi: 'Body Mass Index — a simple measure of body size based on height and weight.',
  sleep: 'Hours of sleep per night. Most older adults need 7-9 hours.',
  steps: 'Daily step count is a rough measure of overall mobility and activity.',
  cognitive: 'MMSE score — a short memory and thinking test. 24-30 is considered normal.',
  mood: 'Your rating of mood or pain from 1 (very poor) to 10 (excellent).',
};

// Where a reading came from. Drives both the record form picker and the badge
// shown on every saved reading, so the two can never drift apart.
const SOURCE_META: Record<string, { key: string; icon: typeof faPenToSquare; bg: string; fg: string }> = {
  manual: { key: 'sourceManual', icon: faPenToSquare, bg: '#f1f5f9', fg: '#475569' },
  wearable: { key: 'sourceWearable', icon: faStopwatch, bg: '#ccfbf1', fg: '#0f766e' },
  iot: { key: 'sourceIot', icon: faMicrochip, bg: '#e0e7ff', fg: '#4338ca' },
  app: { key: 'sourceApp', icon: faMobileScreen, bg: '#ede9fe', fg: '#6d28d9' },
};
const SOURCE_KEYS = Object.keys(SOURCE_META);

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                         */
/* ------------------------------------------------------------------ */

const ElderlyCare: React.FC = () => {
  const navigate = useNavigate();
  const { isViewingAsOther, activeProfile } = useProfile();
  const { canAccess } = useServiceAccess();
  const { user } = useAuth();
  const { t } = useTranslation(['elderly', 'common']);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return t === 'reminders' ? 'reminders' : 'vitals';
  });
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'info' } | null>(null);

  // ─── Admin-managed content (categories + services) ─────
  const [categories, setCategories] = useState<ElderlyCareCategory[]>([]);

  useEffect(() => {
    let active = true;
    elderlyCareService
      .getContent()
      .then((cats) => {
        if (active && Array.isArray(cats) && cats.length) setCategories(cats);
      })
      .catch((err) => {
        console.error('[ElderlyCare] failed to load admin content, using fallback', err);
      });
    return () => { active = false; };
  }, []);

  // ─── Enquire modal state ────────────────────────────────
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiringService, setEnquiringService] = useState<ServiceCardItem | null>(null);
  const [enquiryForm, setEnquiryForm] = useState<{
    dependant: number | null;
    name: string;
    phone: string;
    email: string;
    preferred_time: string;
    message: string;
  }>({ dependant: null, name: '', phone: '', email: '', preferred_time: '', message: '' });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiryError, setEnquiryError] = useState<string | null>(null);

  // ─── Family member selector ─────────────────────────────
  const [elderlyMembers, setElderlyMembers] = useState<{ id: number | null; name: string; relationship: string; age: number | null; is_elderly: boolean }[]>([]);
  const [selectedMember, setSelectedMember] = useState<number | null>(null); // null = no selection
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [userIsElderly, setUserIsElderly] = useState(false);
  const [userAge, setUserAge] = useState<number | null>(null);
  const [isPrimary, setIsPrimary] = useState(true);

  useEffect(() => {
    if (isViewingAsOther) {
      setSelectedMember(null);
    }
    elderlyCareService.getMembers()
      .then((data) => {
        const members = [...data.members];
        setUserIsElderly(data.user_is_elderly);
        setUserAge(data.user_age);
        setIsPrimary(data.is_primary);
        if (data.user_is_elderly) {
          members.push({
            id: null,
            name: user?.name || 'Self',
            relationship: 'Self',
            dob: null,
            age: data.user_age,
            gender: null,
            is_elderly: true,
            has_linked_user: false,
          });
        }
        setElderlyMembers(members);
        if (!isViewingAsOther && members.length > 0) {
          setSelectedMember(members[0].id);
        }
        setMembersLoaded(true);
      })
      .catch(() => {
        setElderlyMembers([]);
        setSelectedMember(null);
        setMembersLoaded(true);
      });
  }, [activeProfile]);

  // Dependant ID to pass to API — undefined means self, number means a dependant
  const selectedDependant: number | undefined = selectedMember ?? undefined;
  const hasElders = elderlyMembers.length > 0;

  // Digital features live status
  // NOTE: Digital Features sub-tab hidden for now (duplicates Reminders/other
  // routes). Code kept below in comments; re-enable via git history if needed.
  // const [digitalStatus, setDigitalStatus] = useState<Record<string, { active: boolean; detail: string; data?: { label: string; value: string; color: string }[] }>>({});

  // Vital metrics state
  const [vitalsSubTab, setVitalsSubTab] = useState<'overview' | 'comparison' | 'digital' | 'monitoring'>('overview');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['heart_rate', 'spo2']);
  const [trendDays, setTrendDays] = useState(14);
  const [trendData, setTrendData] = useState<TrendSeriesByMetric>({});
  const [trendLoading, setTrendLoading] = useState(false);
  const [latestVitals, setLatestVitals] = useState<any>(null);
  const latestVitalsReqRef = useRef<number | null>(null);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [vitalHistory, setVitalHistory] = useState<any[]>([]);
  const [vitalHistoryLoading, setVitalHistoryLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showRecordVital, setShowRecordVital] = useState(false);
  const [vitalForm, setVitalForm] = useState<ElderlyVitalPayload>({
    metric_type: 'temperature', value: 0, value_secondary: null, unit: '\u00B0F', source: 'manual', notes: '', recorded_at: '',
  });
  const [vitalSubmitting, setVitalSubmitting] = useState(false);

  // Reminders state
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const defaultReminderForm: ReminderPayload = { title: '', description: '', reminder_datetime: '', category: 'medication', priority: 'medium', recurrence: 'one_time', notes: '' };
  const [reminderForm, setReminderForm] = useState<ReminderPayload>(defaultReminderForm);
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  // Remote monitoring state
  // NOTE: Remote Monitoring sub-tab hidden for now (its content duplicates the
  // Overview vitals/alerts and redirects to other routes). Re-enable via git.
  // const [monitoringData, setMonitoringData] = useState<{
  //   vitals: any;
  //   wearableData: any[];
  //   upcomingReminders: any[];
  //   alerts: { metric: string; value: string; message: string; color: string }[];
  //   stats: { vitals_tracked?: number } | null;
  // }>({ vitals: null, wearableData: [], upcomingReminders: [], alerts: [], stats: null });
  // const [monitoringLoading, setMonitoringLoading] = useState(false);
  // const [healthScoreData, setHealthScoreData] = useState<{ risk_score: number | null; risk_category: string | null; health_score: number | null; generated_at: string | null }>({ risk_score: null, risk_category: null, health_score: null, generated_at: null });

  // Stat cards state
  const [statCards, setStatCards] = useState<any>(null);

  const [vitalAlerts, setVitalAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const fetchVitalAlerts = async (depId?: number) => {
    setAlertsLoading(true);
    try {
      const data = await elderlyCareService.getAlerts({ unresolved: true, dependant: depId });
      setVitalAlerts(data);
    } catch {
      setVitalAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  };
  useEffect(() => {
    if (!membersLoaded) return;
    if (activeTab !== 'vitals') return;
    const depId = selectedMember ?? undefined;
    fetchVitalAlerts(depId);
  }, [activeTab, vitalsSubTab, selectedMember, membersLoaded, activeProfile]);

  const handleMarkAlertRead = async (id: number) => {
    try {
      await elderlyCareService.markAlertRead(id);
      setVitalAlerts((prev) => prev.map((a) => a.id === id ? { ...a, is_read: true } : a));
      fetchStatCards(selectedMember ?? undefined);
    } catch {
      // non-critical
    }
  };
  const handleResolveAlert = async (id: number) => {
    try {
      await elderlyCareService.resolveAlert(id);
      setVitalAlerts((prev) => prev.filter((a) => a.id !== id));
      fetchStatCards(selectedMember ?? undefined);
    } catch {
      // non-critical
    }
  };

  const fetchStatCards = async (depId?: number) => {
    try {
      const data = await elderlyCareService.getStats(depId);
      setStatCards(data);
    } catch {
      setStatCards(null);
    }
  };
  useEffect(() => {
    if (!membersLoaded) return;
    fetchStatCards(selectedMember ?? undefined);
  }, [selectedMember, membersLoaded, activeProfile]);

  /* --- DISABLED: Digital Features sub-tab hidden (its cards only deep-link to
     other routes). Re-enable via git history if the sub-tab returns.
  // Fetch digital features live status with actual data.
  // Elder mode: only elder-relevant cards are shown (reminders + vitals/
  // analytics), scoped to the selected elder. Self-mode-only cards (wearables,
  // reports, AI) are hidden for elders since they test the caregiver/self
  // profile — their status is cleared and scoped fetches are skipped.
  useEffect(() => {
    let cancelled = false;
    if (!membersLoaded) return () => { cancelled = true; };
    if (activeTab === 'vitals' && vitalsSubTab === 'digital') {
      const depId = selectedMember ?? undefined;

      // In elder mode the self-only cards are hidden, so clear any stale state
      if (depId) {
        setDigitalStatus((prev) => ({ ...prev, wearables: { active: false, detail: 'Your profile only' }, reports: { active: false, detail: 'Your profile only' }, ai: { active: false, detail: 'Your profile only' } }));
      }

      // Reminders + vitals totals — elder-scoped via the backend stats action,
      // which scopes records (incl. linked-elder-account Reminders/Health
      // Records) to the selected elder, matching the Reminders tab & stat cards.
      elderlyCareService.getStats(depId).then((s) => {
        if (cancelled) return;
        const tracked = s?.vitals_tracked ?? 0;
        const up = s?.upcoming_reminders ?? 0;
        const active = s?.active_reminders ?? 0;
        setDigitalStatus((prev) => ({
          ...prev,
          analytics: { active: tracked > 0, detail: tracked > 0 ? `${tracked} vitals tracked` : 'No vitals data yet' },
          reminders: { active: active > 0, detail: active > 0 ? (up > 0 ? `${up} upcoming · ${active} active` : `${active} active`) : 'No reminders set' },
        }));
      }).catch(() => { if (!cancelled) setDigitalStatus((prev) => ({ ...prev, reminders: { active: false, detail: 'Set up reminders' }, analytics: { active: false, detail: 'Start recording vitals' } })); });

      // Wearables, Reports & AI are self-profile only — fetch them only when no
      // elder is selected (hidden in elder mode to avoid caregiver data leaking
      // into the elder's view).
      if (!depId) {
        axiosInstance.get(API_ENDPOINTS.wearables.dataLatest).then((res) => {
          if (cancelled) return;
          const items = Array.isArray(res.data) ? res.data : res.data.results || [];
          setDigitalStatus((prev) => ({ ...prev, wearables: { active: items.length > 0, detail: items.length > 0 ? `${items.length} readings synced` : 'No devices connected' } }));
        }).catch(() => { if (!cancelled) setDigitalStatus((prev) => ({ ...prev, wearables: { active: false, detail: 'Connect a device' } })); });

        // Health summary (self only)
        axiosInstance.get(API_ENDPOINTS.healthSummary.latest).then((res) => {
          if (cancelled) return;
          const s = res.data;
          setDigitalStatus((prev) => ({ ...prev, reports: { active: !!s?.overall_score, detail: s?.overall_score ? 'Report available' : 'Generate your first report' } }));
        }).catch(() => { if (!cancelled) setDigitalStatus((prev) => ({ ...prev, reports: { active: false, detail: 'Generate your first report' } })); });

        // Disease prediction (self only)
        axiosInstance.get('/api/disease-prediction/latest/').then((res) => {
          if (cancelled) return;
          const p = res.data;
          setDigitalStatus((prev) => ({ ...prev, ai: { active: p?.overall_risk_score != null, detail: p?.overall_risk_score != null ? 'Prediction available' : 'Run first prediction' } }));
        }).catch(() => { if (!cancelled) setDigitalStatus((prev) => ({ ...prev, ai: { active: false, detail: 'Run first prediction' } })); });
      }
    }
    return () => { cancelled = true; };
  }, [activeTab, vitalsSubTab, selectedMember, membersLoaded, activeProfile, latestVitals]);
  */

  /* --- DISABLED: Remote Monitoring sub-tab hidden (content duplicates Overview
     vitals/alerts and only redirects elsewhere). Re-enable via git history.
  // Fetch remote monitoring data.
  // Elder mode: vitals come from the elder-scoped merged vitals, alerts are the
  // real backend-generated elder alerts, reminders are elder-scoped. Self-only
  // data (wearables, AI health score) is fetched only for self mode.
  useEffect(() => {
    let cancelled = false;
    if (membersLoaded && activeTab === 'vitals' && vitalsSubTab === 'monitoring') {
      setMonitoringLoading(true);
      const results: typeof monitoringData = { vitals: null, wearableData: [], upcomingReminders: [], alerts: [], stats: null };
      const promises: Promise<any>[] = [];
      const depId = selectedMember ?? undefined;

      if (depId) {
        // Elder mode — latest merged vitals are already elder-scoped (latestVitals)
        results.vitals = latestVitals;

        // Real backend alerts generated when the elder's vitals were recorded
        promises.push(
          elderlyCareService.getAlerts({ unresolved: true, dependant: depId }).then((alerts: any[]) => {
            results.alerts = alerts.slice(0, 6).map((a: any) => {
              const sevColor = a.severity === 'critical' ? '#ef4444' : a.severity === 'warning' ? '#f59e0b' : '#3b82f6';
              const rec = a.vital_record || {};
              let value = rec.value != null ? String(rec.value) : '';
              if (rec.value_secondary != null) value += `/${rec.value_secondary}`;
              return { metric: (rec.metric_type || 'Vital').replace(/_/g, ' '), value, message: a.message, color: sevColor };
            });
          }).catch(() => {}),
        );

        // AI health score has no elder-scoped source — clear it (step hidden in elder mode)
        setHealthScoreData({ risk_score: null, risk_category: null, health_score: null, generated_at: null });
      } else {
        results.vitals = latestVitals;

        // Self mode — heuristic alerts from latest vitals
        const v = latestVitals;
        if (v?.heartRate?.value > 100) results.alerts.push({ metric: 'Heart Rate', value: `${v.heartRate.value} bpm`, message: 'Elevated heart rate detected. Consider resting.', color: '#ef4444' });
        if (v?.heartRate?.value < 60) results.alerts.push({ metric: 'Heart Rate', value: `${v.heartRate.value} bpm`, message: 'Low heart rate. Monitor closely.', color: '#f59e0b' });
        if (v?.bloodPressure?.systolic >= 140) results.alerts.push({ metric: 'Blood Pressure', value: `${v.bloodPressure.systolic}/${v.bloodPressure.diastolic}`, message: 'High BP reading. Rest and re-measure in 15 min.', color: '#ef4444' });
        if (v?.oxygenSaturation?.value < 95) results.alerts.push({ metric: 'SpO2', value: `${v.oxygenSaturation.value}%`, message: 'Low oxygen saturation. Seek medical attention.', color: '#ef4444' });
        if (v?.glucose?.value > 140) results.alerts.push({ metric: 'Blood Sugar', value: `${v.glucose.value} mg/dL`, message: 'Elevated blood sugar. Review diet and medication.', color: '#f59e0b' });

        // Wearable data (self only)
        promises.push(
          axiosInstance.get(API_ENDPOINTS.wearables.dataLatest).then((res) => {
            const d = res.data;
            results.wearableData = Array.isArray(d) ? d : d.results || [];
          }).catch(() => {}),
        );

        // Health score & disease prediction (self only)
        promises.push(
          Promise.all([
            axiosInstance.get('/api/disease-prediction/latest/').catch(() => ({ data: null })),
            axiosInstance.get(API_ENDPOINTS.healthSummary.latest).catch(() => ({ data: null })),
          ]).then(([predRes, summaryRes]) => {
            const pred = predRes.data;
            const summary = summaryRes.data;
            setHealthScoreData({
              risk_score: pred?.overall_risk_score ?? null,
              risk_category: pred?.overall_risk_category ?? null,
              health_score: summary?.overall_score ?? null,
              generated_at: pred?.created_at || summary?.created_at || null,
            });
          }),
        );
      }

      // Reminders (elder-scoped when an elder is selected)
      promises.push(
        reminderService.getReminders(depId).then((r) => {
          const active = r.filter((rem) => !rem.is_completed);
          results.upcomingReminders = active.slice(0, 5);
        }).catch(() => {}),
      );

      // Elder-scoped vitals stats — supplies the tracked-vitals count for the family card
      promises.push(
        elderlyCareService.getStats(depId).then((s) => {
          results.stats = s || null;
        }).catch(() => {}),
      );

      Promise.all(promises).finally(() => {
        if (cancelled) return;
        setMonitoringData(results);
        setMonitoringLoading(false);
      });
    }
    return () => { cancelled = true; };
  }, [activeTab, vitalsSubTab, selectedMember, membersLoaded, activeProfile, latestVitals]);
  */

  const fetchReminders = async (depId?: number) => {
    setRemindersLoading(true);
    try {
      const data = await reminderService.getReminders(depId);
      setReminders(data);
    } catch {
      setReminders([]);
    } finally {
      setRemindersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'reminders' && membersLoaded) {
      fetchReminders(selectedMember ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMember, membersLoaded, activeProfile]);

  // Only elderly-specific metrics — common vitals (heart rate, BP, blood sugar, SpO2, weight, BMI)
  // are tracked via Health Records and auto-fetched into the vital metrics table.
  type FormMetric = { id: string; primaryMetricType?: string; label: string; icon: any; unit: string; placeholder: string; valueLabel: string; color: string; hasSecondary?: boolean; secondaryLabel?: string; secondaryPlaceholder?: string; pairId?: string; pairUnit?: string };

  // Elderly-specific metrics \u2014 always available (no Health Records equivalent).
  const elderlyOnlyMetrics: FormMetric[] = [
    { id: 'temperature', label: 'Body Temperature', icon: faThermometerHalf, unit: '\u00B0F', placeholder: 'e.g. 98.6', valueLabel: 'Body Temperature (\u00B0F)', color: '#f97316' },
    { id: 'sleep', label: 'Sleep Duration', icon: faBed, unit: 'hours', placeholder: 'e.g. 7.5', valueLabel: 'Sleep Duration (hours)', color: '#8b5cf6' },
    { id: 'steps', label: 'Steps / Mobility', icon: faWalking, unit: 'steps', placeholder: 'e.g. 4000', valueLabel: 'Daily Step Count', color: '#ec4899' },
    { id: 'cognitive', label: 'Cognitive Score', icon: faBrain, unit: 'score', placeholder: 'e.g. 28', valueLabel: 'MMSE Score (0-30)', color: '#6366f1' },
    { id: 'mood', label: 'Mood / Pain', icon: faSmile, unit: 'scale 1-10', placeholder: 'e.g. 7', valueLabel: 'Mood / Pain Level (1-10)', color: '#14b8a6' },
  ];

  const commonVitalMetrics: FormMetric[] = [
    { id: 'heart_rate', label: 'Heart Rate', icon: faHeartbeat, unit: 'bpm', placeholder: 'e.g. 78', valueLabel: 'Heart Rate (bpm)', color: '#ef4444' },
    { id: 'blood_pressure', label: 'Blood Pressure', icon: faTint, unit: 'mmHg', placeholder: 'Systolic e.g. 120', valueLabel: 'Systolic (mmHg)', color: '#3b82f6', hasSecondary: true, secondaryLabel: 'Diastolic (mmHg)', secondaryPlaceholder: 'e.g. 80' },
    { id: 'blood_sugar', label: 'Blood Sugar', icon: faTint, unit: 'mg/dL', placeholder: 'e.g. 95', valueLabel: 'Blood Sugar (mg/dL)', color: '#f59e0b' },
    { id: 'spo2_rr', primaryMetricType: 'spo2', label: 'SpO2 & Respiratory Rate', icon: faLungs, unit: '%', placeholder: 'e.g. 97', valueLabel: 'SpO2 / Oxygen (%)', color: '#06b6d4', hasSecondary: true, secondaryLabel: 'Respiratory Rate (breaths/min)', secondaryPlaceholder: 'e.g. 16', pairId: 'respiratory_rate', pairUnit: 'breaths/min' },
    { id: 'weight_bmi', primaryMetricType: 'weight', label: 'Weight & BMI', icon: faWeight, unit: 'kg', placeholder: 'e.g. 65', valueLabel: 'Weight (kg)', color: '#10b981', hasSecondary: true, secondaryLabel: 'BMI', secondaryPlaceholder: 'e.g. 22.5', pairId: 'bmi', pairUnit: 'kg/m\u00B2' },
  ];

  const formMetricConfig: FormMetric[] = selectedDependant
    ? [...commonVitalMetrics, ...elderlyOnlyMetrics]
    : elderlyOnlyMetrics;

  const activeFormMetric = formMetricConfig.find((m) => m.id === vitalForm.metric_type) || formMetricConfig[0];
  const activeSecondaryUnit = activeFormMetric.pairUnit || activeFormMetric.unit;

  useEffect(() => {
    if (!formMetricConfig.find((m) => m.id === vitalForm.metric_type)) {
      const first = formMetricConfig[0];
      setVitalForm((p) => ({ ...p, metric_type: first.id, unit: first.unit, value: 0, value_secondary: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDependant]);

  const handleVitalSubmit = async () => {
    const primaryValue = Number(vitalForm.value);
    const secondaryValue = vitalForm.value_secondary == null ? null : Number(vitalForm.value_secondary);
    const hasPrimary = Number.isFinite(primaryValue) && primaryValue > 0;
    const hasSecondary = secondaryValue != null && Number.isFinite(secondaryValue) && secondaryValue > 0;
    const pair = activeFormMetric.pairId && activeFormMetric.pairUnit
      ? { id: activeFormMetric.pairId, unit: activeFormMetric.pairUnit }
      : null;
    const requiresInlineSecondary = Boolean(activeFormMetric.hasSecondary && !pair);

    if ((!hasPrimary && !(pair && hasSecondary)) || (requiresInlineSecondary && !hasSecondary)) {
      setToastMsg({ message: t('elderly:vitals.valueRequired'), type: 'info' });
      setTimeout(() => setToastMsg(null), 3000);
      return;
    }

    setVitalSubmitting(true);
    try {
      const recordedAt = vitalForm.recorded_at && vitalForm.recorded_at.length === 16
        ? `${vitalForm.recorded_at}:00`
        : vitalForm.recorded_at || '';
      const base = {
        source: vitalForm.source,
        notes: vitalForm.notes,
        dependant: selectedDependant ?? null,
        recorded_at: recordedAt,
      };
      const primaryMetricType = activeFormMetric.primaryMetricType || activeFormMetric.id;
      const records: ElderlyVitalPayload[] = [];

      if (hasPrimary) {
        records.push({
          ...base,
          metric_type: primaryMetricType,
          value: primaryValue,
          value_secondary: requiresInlineSecondary ? secondaryValue : null,
          unit: activeFormMetric.unit,
        });
      }
      if (pair && hasSecondary) {
        records.push({
          ...base,
          metric_type: pair.id,
          value: secondaryValue as number,
          value_secondary: null,
          unit: pair.unit,
        });
      }

      if (records.length > 1) {
        await elderlyCareService.createVitals(records);
      } else if (records.length === 1) {
        await elderlyCareService.createVital(records[0]);
      }

      setShowRecordVital(false);
      const first = formMetricConfig[0];
      setVitalForm({ metric_type: first.id, value: 0, value_secondary: null, unit: first.unit, source: 'manual', notes: '', recorded_at: '' });
      setToastMsg({ message: t('elderly:vitals.vitalRecorded'), type: 'success' });
      setTimeout(() => setToastMsg(null), 3000);
      fetchElderlyVitals(selectedDependant);
      fetchVitalAlerts(selectedDependant);
      fetchStatCards(selectedDependant);
    } catch (err: any) {
      const data = err?.response?.data;
      let msg = t('elderly:vitals.vitalFailed');
      if (data) {
        if (typeof data === 'string') msg = data;
        else if (data.detail) msg = data.detail;
        else {
          const firstField = Object.keys(data)[0];
          if (firstField) {
            const v = data[firstField];
            const text = Array.isArray(v) ? v[0] : v;
            msg = `${firstField}: ${text}`;
          }
        }
      }
      setToastMsg({ message: msg, type: 'info' });
      setTimeout(() => setToastMsg(null), 5000);
    } finally {
      setVitalSubmitting(false);
    }
  };

  // Fetch latest vitals from both elderly care AND health records APIs, then merge
  const fetchElderlyVitals = async (depId?: number) => {
    const reqDep = depId ?? null;
    latestVitalsReqRef.current = reqDep;
    setVitalsLoading(true);
    try {
      const [elderlyData, healthRes] = await Promise.allSettled([
        elderlyCareService.getLatestAll(depId),
        // Health records only for self (no dependant support)
        depId ? Promise.reject('skip') : axiosInstance.get(API_ENDPOINTS.healthRecords.latestAll),
      ]);

      const elderly = elderlyData.status === 'fulfilled' ? elderlyData.value : {};
      const health = healthRes.status === 'fulfilled' ? healthRes.value.data : {};

      // Merge: Health Records (camelCase) supply common vitals; elderly care
      // records fill elderly-specific gaps. When both sources record the same
      // metric, the most recent recording by timestamp wins, so the displayed
      // "latest" value is genuinely the newest reading across all tabs.
      const merged: any = {};

      const latestTs = (rec: any): number => {
        const t = rec?.recorded_at ?? rec?.updated_at;
        return t ? new Date(t).getTime() : 0;
      };

      // Resolve one common metric: snakeKey = elderly-care key, camelKey = health key.
      // In elder mode the health value rides inside the latest_all response; in
      // self mode it also comes from the separate health records fetch.
      const resolveLatest = (
        snakeKey: string,
        camelKey: string,
        toCamel: (el: any) => any,
        toSnake: (h: any) => any,
      ) => {
        const el = elderly[snakeKey];
        const h = elderly[camelKey] ?? health[camelKey];
        if (!el && !h) return;
        if (el && (!h || latestTs(el) >= latestTs(h))) {
          // Elderly-care recording is the newest — it wins both display keys
          merged[snakeKey] = el;
          merged[camelKey] = toCamel(el);
        } else {
          // Health record is the newest — it wins both display keys.
          // Carry the health timestamp onto the snake shape so the vitals
          // form can prefill the last recorded time for the metric.
          merged[camelKey] = h;
          merged[snakeKey] = { ...toSnake(h), recorded_at: h.recorded_at ?? h.updated_at };
        }
      };

      resolveLatest('heart_rate', 'heartRate',
        (el) => ({ value: el.value, unit: el.unit || 'bpm' }),
        (h) => ({ value: h.value, unit: h.unit || 'bpm', value_secondary: null }));

      resolveLatest('blood_pressure', 'bloodPressure',
        (el) => ({ systolic: el.value, diastolic: el.value_secondary, unit: el.unit || 'mmHg' }),
        (h) => ({ value: h.systolic, value_secondary: h.diastolic, unit: 'mmHg' }));

      resolveLatest('blood_sugar', 'glucose',
        (el) => ({ value: el.value, unit: el.unit || 'mg/dL' }),
        (h) => ({ value: h.value, unit: h.unit || 'mg/dL', value_secondary: null }));

      resolveLatest('spo2', 'oxygenSaturation',
        (el) => ({ value: el.value, unit: el.unit || '%' }),
        (h) => ({ value: h.value, unit: h.unit || '%', value_secondary: null }));

      resolveLatest('weight', 'weight',
        (el) => ({ value: el.value, unit: el.unit || 'kg' }),
        (h) => ({ value: h.value, unit: h.unit || 'kg', value_secondary: null }));

      resolveLatest('bmi', 'bmi',
        (el) => ({ value: el.value, unit: el.unit || 'BMI' }),
        (h) => ({ value: h.value, unit: h.unit || 'BMI', value_secondary: null }));

      // Elderly-care-only metrics fill in where no health record exists
      Object.keys(elderly).forEach((key) => {
        if (!merged[key]) {
          merged[key] = elderly[key];
        }
      });

      if (latestVitalsReqRef.current === reqDep) {
        setLatestVitals(merged);
      }
      return merged;
    } catch {
      if (latestVitalsReqRef.current === reqDep) {
        setLatestVitals(null);
      }
      return null;
    } finally {
      setVitalsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (activeTab === 'vitals' && membersLoaded) {
      const depId = selectedMember ?? undefined;
      fetchElderlyVitals(depId).then((merged) => {
        if (!cancelled) setLatestVitals(merged);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [activeTab, selectedMember, membersLoaded, activeProfile]);

  // Fetch recently recorded vitals for the history panel
  useEffect(() => {
    let cancelled = false;
    if (activeTab === 'vitals' && membersLoaded) {
      setVitalHistoryLoading(true);
      elderlyCareService
        .getVitals({ dependant: selectedDependant })
        .then((recs) => {
          if (!cancelled) setVitalHistory(recs || []);
        })
        .catch(() => {
          if (!cancelled) setVitalHistory([]);
        })
        .finally(() => {
          if (!cancelled) setVitalHistoryLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [activeTab, selectedMember, membersLoaded, activeProfile, latestVitals]);

  // Fetch raw trend data for comparison from backend. Raw recordings are stored
  // (so latest / avg / min / max match the recorded vitals exactly); the chart
  // itself daily-buckets. Fetched only when the comparison tab is visible and
  // the profile/members have loaded (avoids firing before auth is ready).
  useEffect(() => {
    if (activeTab !== 'vitals' || vitalsSubTab !== 'comparison' || !membersLoaded || !activeProfile) return;

    let cancelled = false;
    setTrendData({});
    setTrendLoading(true);

    const depId = selectedMember ?? undefined;

    const requests = selectedMetrics.flatMap((metricId) =>
      (metricBackendKeys[metricId] || [metricId]).map((backendMetric) =>
        axiosInstance
          .get(API_ENDPOINTS.elderlyCare.vitalsTrend, {
            params: { metric_type: backendMetric, days: trendDays, ...(depId ? { dependant: depId } : {}) },
          })
          .then((res) => ({ metricId, backendMetric, data: res.data }))
          .catch(() => ({ metricId, backendMetric, data: [] })),
      ),
    );

    Promise.all(requests)
      .then((responses) => {
        if (cancelled) return;
        const next: TrendSeriesByMetric = {};
        responses.forEach((resp) => {
          if (!next[resp.metricId]) next[resp.metricId] = {};
          next[resp.metricId][resp.backendMetric] = toTrendPoints(resp.data).sort((a, b) => (a.ts && b.ts && a.ts < b.ts ? -1 : 1));
        });
        setTrendData(next);
      })
      .finally(() => {
        if (!cancelled) setTrendLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, vitalsSubTab, selectedMetrics, trendDays, selectedMember, membersLoaded, activeProfile]);

  /* --- Toggle metric selection for comparison --- */
  const toggleMetricSelection = (id: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : prev.length < 4 ? [...prev, id] : prev,
    );
  };

  /* --- Get feasibility badge --- */

  /* --- Get latest value for a vital metric from API data --- */
  const getLatestValue = (metricId: string): string => {
    if (!latestVitals) return '--';

    // Special case: SpO2 & Respiratory Rate combined display
    if (metricId === 'spo2') {
      const spo2Val = latestVitals.oxygenSaturation?.value ?? latestVitals['spo2']?.value;
      const rrVal = latestVitals['respiratory_rate']?.value;
      if (spo2Val != null && rrVal != null) return `${spo2Val} / ${rrVal}`;
      if (spo2Val != null) return spo2Val.toString();
      if (rrVal != null) return `-- / ${rrVal}`;
      return '--';
    }

    // Special case: Weight / BMI combined display (row shows both together)
    if (metricId === 'weight_bmi') {
      const wVal = latestVitals.weight?.value;
      const bVal = latestVitals.bmi?.value;
      if (wVal != null && bVal != null) return `${wVal} / ${bVal}`;
      if (wVal != null) return `${wVal}`;
      if (bVal != null) return `${bVal}`;
      return '--';
    }

    // Health Records (camelCase) are the primary source; elderly care fills gaps.
    // This keeps Overview consistent with Digital / Monitoring tabs.
    const healthKeys: Record<string, string[]> = {
      heart_rate: ['heartRate'],
      blood_pressure: ['bloodPressure'],
      blood_sugar: ['glucose'],
    };
    for (const key of healthKeys[metricId] || []) {
      const rec = latestVitals[key];
      if (rec) {
        if (metricId === 'blood_pressure' && rec.systolic != null) return `${rec.systolic}/${rec.diastolic}`;
        if (rec.value_secondary != null) return `${rec.value}/${rec.value_secondary}`;
        if (rec.value != null) return rec.value.toString();
      }
    }

    // Fallback to elderly care API format (snake_case keys)
    const keyMap: Record<string, string[]> = {
      weight_bmi: ['weight_bmi', 'bmi', 'weight'],
      mobility: ['mobility', 'steps'],
    };
    const keysToCheck = keyMap[metricId] || [metricId];

    for (const key of keysToCheck) {
      const elderlyRecord = latestVitals[key];
      if (elderlyRecord) {
        if (elderlyRecord.value_secondary != null) return `${elderlyRecord.value}/${elderlyRecord.value_secondary}`;
        return elderlyRecord.value?.toString() || '--';
      }
    }

    return '--';
  };

  // Unit for a metric in the overview table/cards. The SpO2 row doubles as a
  // "SpO2 & Respiratory Rate" combined row, so its unit depends on whether a
  // respiratory rate value is present.
  const getMetricUnit = (metric: VitalMetric): string => {
    if (metric.id === 'spo2') return '% / breaths/min';
    if (metric.id === 'weight_bmi') return 'kg / kg/m²';
    return metricChartMeta[metric.id]?.series[0]?.unit || metric.unit;
  };

  /* --- Chart group: 1 line, or 2 lines sharing one axis (same unit) --- */
  interface PreparedSeries {
    key: string;
    name: string;
    unit: string;
    color: string;
    band?: [number, number];
    data: ChartPoint[];
  }

  // Merge every line in a group onto one day-by-day axis so the readings line up.
  const buildChartRows = (group: PreparedSeries[]): ChartRow[] => {
    const keys = group.map((s) => s.key);
    const stamps = group.flatMap((s) => s.data.map((p) => p.ts).filter(Boolean) as string[]);
    if (!stamps.length) return [];

    const byDay = new Map<string, Map<string, number | null>>();
    group.forEach((s) => {
      const m = new Map<string, number | null>();
      s.data.forEach((p) => {
        if (p.ts) m.set(new Date(p.ts).toDateString(), p.value);
      });
      byDay.set(s.key, m);
    });

    const times = stamps.map((ts) => new Date(ts).getTime());
    const cur = new Date(Math.min(...times));
    const end = new Date(Math.max(...times));
    cur.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const rows: ChartRow[] = [];
    while (cur <= end) {
      const dayKey = cur.toDateString();
      const row: ChartRow = { date: cur.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) };
      keys.forEach((k) => {
        row[k] = byDay.get(k)!.get(dayKey) ?? null;
      });
      rows.push(row);
      cur.setDate(cur.getDate() + 1);
    }
    return downsampleRows(rows, keys);
  };

  const statusWord = (status: ReadingStatus) => {
    if (status === 'high') return t('elderly:vitals.aboveUsual');
    if (status === 'low') return t('elderly:vitals.belowUsual');
    if (status === 'normal') return t('elderly:vitals.withinUsual');
    return null;
  };

  const renderTrendTooltip = (active: boolean, payload: any, label: any, group: PreparedSeries[]) => {
    if (!active || !payload || !payload.length) return null;
    const rows = payload.filter((p: any) => p.value != null);
    return (
      <div className="ec-trend-tip">
        <strong className="ec-trend-tip-date">{label}</strong>
        {rows.length ? (
          rows.map((p: any) => {
            const meta = group.find((s) => s.key === p.dataKey);
            if (!meta) return null;
            const word = statusWord(classifyReading(p.value, meta.band));
            return (
              <div key={p.dataKey} className="ec-trend-tip-row">
                <span className="ec-trend-dot" style={{ background: meta.color }} />
                <span className="ec-trend-tip-name">{meta.name}</span>
                <span className="ec-trend-tip-value">
                  {fmtNum(p.value)} <em>{meta.unit}</em>
                </span>
                {word && <span className={`ec-trend-tip-status ${classifyReading(p.value, meta.band)}`}>{word}</span>}
              </div>
            );
          })
        ) : (
          <span className="ec-trend-tip-empty">{t('elderly:vitals.noReadingOn')}</span>
        )}
      </div>
    );
  };

  /* --- Smooth line over a shaded "usual range" band --- */
  const renderTrendChart = (group: PreparedSeries[]) => {
    if (!group.length) return null;
    const rows = buildChartRows(group);
    if (!rows.length) return null;

    const keys = group.map((s) => s.key);
    const values = rows
      .flatMap((r) => keys.map((k) => r[k]))
      .filter((v): v is number => typeof v === 'number');
    if (!values.length) return null;

    const bands = group.map((s) => s.band).filter(Boolean) as [number, number][];
    const bandLabels = group.filter((s) => s.band).map((s) => s.name);
    const domain = buildYDomain(values, bands);
    const isMulti = group.length > 1;
    const hasGap = rows.some((r) => keys.some((k) => r[k] == null));
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    // A line needs two points to read as a trend; with one or two readings show
    // the markers instead, otherwise the chart would look empty.
    const dot = values.length <= 2
      ? { r: 4.5, fill: group[0].color, stroke: '#ffffff', strokeWidth: 2 }
      : false;

    return (
      <div className="ec-trend-chart">
        <div className="ec-trend-chart-head">
          {isMulti ? (
            <div className="ec-trend-legend">
              {group.map((s) => (
                <span key={s.key} className="ec-trend-legend-item">
                  <span className="ec-trend-dot" style={{ background: s.color }} />
                  {s.name}
                </span>
              ))}
            </div>
          ) : (
            <span className="ec-trend-chart-title">{group[0].name}</span>
          )}
          <span className="ec-trend-chart-unit">{group[0].unit}</span>
        </div>

        <div className={`ec-trend-chart-plot${isMulti ? ' short' : ''}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 12, right: 14, bottom: 0, left: -18 }}>
              <defs>
                {group.map((s) => (
                  <linearGradient key={`ec-grad-${s.key}`} id={`ec-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />

              {bands.map((band, i) => (
                <ReferenceArea
                  key={`ec-band-${i}`}
                  y1={band[0]}
                  y2={band[1]}
                  fill="#10b981"
                  fillOpacity={0.1}
                  stroke="#10b981"
                  strokeOpacity={0.35}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                  label={
                    isMulti
                      ? { value: bandLabels[i], position: 'insideTopLeft', fill: '#047857', fontSize: 9, fontFamily: 'Nunito, sans-serif' }
                      : undefined
                  }
                />
              ))}

              {!isMulti && (
                <ReferenceLine
                  y={avg}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  label={{
                    value: `${t('elderly:vitals.average')} ${fmtNum(avg)}`,
                    position: 'insideTopRight',
                    fill: '#94a3b8',
                    fontSize: 10,
                    fontFamily: 'Nunito, sans-serif',
                  }}
                />
              )}

              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Nunito, sans-serif' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                minTickGap={24}
                tickMargin={8}
              />
              <YAxis
                domain={domain}
                width={54}
                tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Nunito, sans-serif' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => fmtNum(v)}
              />

              <Tooltip
                cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                content={({ active, payload, label }) => renderTrendTooltip(active, payload, label, group)}
                wrapperStyle={{ outline: 'none' }}
              />

              {group.map((s) =>
                isMulti ? (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2.5}
                    connectNulls
                    dot={dot}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                    isAnimationActive={false}
                  />
                ) : (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2.5}
                    fill={`url(#ec-grad-${s.key})`}
                    connectNulls
                    dot={dot}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                    isAnimationActive={false}
                  />
                ),
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="ec-trend-chart-foot">
          {bands.length > 0 && (
            <span className="ec-trend-foot-item">
              <span className="ec-trend-band-swatch" />
              {t('elderly:vitals.usualRangeLegend')}
            </span>
          )}
          {hasGap && <span className="ec-trend-foot-item">{t('elderly:vitals.someDaysMissing')}</span>}
        </div>
      </div>
    );
  };

  /* --- Plain-language direction of travel (no percentages) --- */
  const getTrendWord = (data: ChartPoint[], metricId: string) => {
    const steady = { icon: faMinus, key: 'trendSteady', color: '#64748b' };
    const readings = data.filter((d) => d.value != null) as { value: number }[];
    if (readings.length < 2) return steady;
    const vals = readings.map((d) => d.value);
    let diff: number;
    if (vals.length >= 6) {
      const recent = vals.slice(-3).reduce((s, v) => s + v, 0) / 3;
      const older = vals.slice(0, 3).reduce((s, v) => s + v, 0) / 3;
      diff = older ? ((recent - older) / older) * 100 : 0;
    } else {
      const first = vals[0];
      const last = vals[vals.length - 1];
      diff = first ? ((last - first) / first) * 100 : 0;
    }
    const dir = metricClinicDirection[metricId] || 'neutral';
    if (diff > 3) {
      const bad = dir === 'highBad';
      return { icon: faArrowUp, key: 'trendRising', color: bad ? '#ef4444' : dir === 'lowBad' ? '#10b981' : '#0e7490' };
    }
    if (diff < -3) {
      const bad = dir === 'lowBad';
      return { icon: faArrowDown, key: 'trendFalling', color: bad ? '#ef4444' : dir === 'highBad' ? '#10b981' : '#0e7490' };
    }
    return steady;
  };

  const TREND_STATUS_META = {
    normal: { key: 'statusNormal', bg: '#ecfdf5', fg: '#047857', icon: faCheckCircle },
    watch: { key: 'statusWatch', bg: '#fffbeb', fg: '#b45309', icon: faExclamationTriangle },
    alert: { key: 'statusAlert', bg: '#fef2f2', fg: '#b91c1c', icon: faCircleExclamation },
    none: { key: 'statusNoData', bg: '#f1f5f9', fg: '#64748b', icon: faMinus },
  } as const;

  const TREND_PERIODS = [
    { days: 7, key: 'period1Week' },
    { days: 14, key: 'period2Weeks' },
    { days: 30, key: 'period1Month' },
    { days: 90, key: 'period3Months' },
  ];

  const openRecordVital = () => {
    setVitalsSubTab('overview');
    setShowRecordVital(true);
  };

  /* --- Render Vitals Overview Tab --- */
  const renderVitalsOverview = () => (
    <div className="ec-vitals-overview">
      {/* Quick actions — one-tap shortcuts for common tasks */}
      <div className="ec-quick-actions">
        <button className="ec-quick-action" style={{ borderColor: '#0e7490', color: '#0e7490' }} onClick={() => setShowRecordVital(!showRecordVital)}>
          <FontAwesomeIcon icon={showRecordVital ? faTimesCircle : faHeartbeat} />
          {showRecordVital ? t('elderly:vitals.cancelRecord') : t('elderly:vitals.recordVital')}
        </button>
        <button className="ec-quick-action" style={{ borderColor: '#e11d48', color: '#e11d48' }} onClick={() => setActiveTab('reminders')}>
          <FontAwesomeIcon icon={faBell} /> {t('elderly:vitals.addReminder')}
        </button>
        <button className="ec-quick-action" style={{ borderColor: '#0e7490', color: '#0e7490' }} onClick={() => setVitalsSubTab('comparison')}>
          <FontAwesomeIcon icon={faChartLine} /> {t('elderly:vitals.viewTrends')}
        </button>
        {!selectedDependant && (
          <>
            {canAccess('wearable-devices') && (
              <button className="ec-quick-action" style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }} onClick={() => navigate('/wearable-devices')}>
                <FontAwesomeIcon icon={faSync} /> Wearable devices
              </button>
            )}
            {canAccess('health-insights') && (
              <button className="ec-quick-action" style={{ borderColor: '#f59e0b', color: '#f59e0b' }} onClick={() => navigate('/health-summary')}>
                <FontAwesomeIcon icon={faClipboardCheck} /> Reports
              </button>
            )}
          </>
        )}
      </div>

      {/* Overall status — at-a-glance for non-clinical users */}
      <div className={`ec-status-banner${vitalAlerts.length > 0 ? ' warn' : ''}`}>
        <FontAwesomeIcon icon={vitalAlerts.length > 0 ? faExclamationTriangle : faCheckCircle} />
        <div>
          <strong>{vitalAlerts.length > 0 ? t('elderly:vitals.activeAlertsNeed', { n: vitalAlerts.length }) : t('elderly:vitals.allNormal')}</strong>
          <span>
            {selectedDependant
              ? `${elderlyMembers.find((m) => m.id === selectedMember)?.name || 'Your family member'}'s latest readings are shown below.`
              : 'Your latest readings are shown below.'}
          </span>
        </div>
      </div>

      {/* Record Vital Form */}
      {showRecordVital && (
        <div className="ec-add-reminder-form" style={{ marginBottom: 24 }}>
          <h4>
            <FontAwesomeIcon icon={faHeartbeat} /> {t('elderly:vitals.recordElderlyVital')}
            {selectedDependant && (
              <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#f59e0b', marginLeft: 8 }}>
                for {elderlyMembers.find(m => m.id === selectedMember)?.name}
              </span>
            )}
          </h4>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 }}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 6, color: '#0e7490' }} />
            {selectedDependant ? (
              <>Record any vital for this family member. Heart Rate, BP, Blood Sugar, SpO₂ + Respiratory Rate, Weight + BMI and elderly-specific metrics are saved against their profile here.</>
            ) : (
              <>Heart Rate, Blood Pressure, Blood Sugar, SpO2, Weight &amp; BMI are fetched automatically from <strong>Health Records</strong>. Use this form for elderly-specific metrics only.</>
            )}
          </p>
          <div className="ec-form-group" style={{ marginBottom: 18 }}>
            <label>{t('elderly:vitals.metricType')}</label>
            <div className="ec-category-chips">
              {formMetricConfig.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`ec-category-chip${vitalForm.metric_type === m.id ? ' selected' : ''}`}
                  style={vitalForm.metric_type === m.id ? { borderColor: m.color, background: `${m.color}12` } : {}}
                  onClick={() => setVitalForm((p) => ({
                    ...p,
                    metric_type: m.id,
                    unit: m.unit,
                    value: 0,
                    value_secondary: null,
                    recorded_at: '',
                  }))}
                >
                  <FontAwesomeIcon icon={m.icon} style={{ color: vitalForm.metric_type === m.id ? m.color : undefined }} /> {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ec-form-grid">
            <div className="ec-form-column">
              <div className="ec-form-column-title"><FontAwesomeIcon icon={faChartLine} /> {t('elderly:vitals.values')}</div>
              <div className="ec-form-group">
                <label>
                  {activeFormMetric.valueLabel} {!activeFormMetric.pairId && <span className="ec-form-required">*</span>}
                  {activeFormMetric.pairId && <span className="ec-form-hint">{t('elderly:vitals.oneValueRequired')}</span>}
                </label>
                <div className="ec-input-with-unit">
                  <input type="number" step="0.1" placeholder={activeFormMetric.placeholder} aria-label={activeFormMetric.valueLabel} value={vitalForm.value || ''} onChange={(e) => setVitalForm((p) => ({ ...p, value: parseFloat(e.target.value) || 0 }))} />
                  <span>{activeFormMetric.unit}</span>
                </div>
              </div>
              {activeFormMetric.hasSecondary && (
                <div className="ec-form-group">
                  <label>
                    {activeFormMetric.secondaryLabel} {activeFormMetric.pairId || <span className="ec-form-required">*</span>}
                  </label>
                  <div className="ec-input-with-unit">
                    <input type="number" step="0.1" placeholder={activeFormMetric.secondaryPlaceholder} aria-label={activeFormMetric.secondaryLabel} value={vitalForm.value_secondary || ''} onChange={(e) => setVitalForm((p) => ({ ...p, value_secondary: parseFloat(e.target.value) || null }))} />
                    <span>{activeSecondaryUnit}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="ec-form-column">
              <div className="ec-form-column-title"><FontAwesomeIcon icon={faClock} /> {t('elderly:vitals.details')}</div>
              <div className="ec-form-group">
                <label>{t('elderly:vitals.recordedAt')}</label>
                <input type="datetime-local" value={vitalForm.recorded_at} onChange={(e) => setVitalForm((p) => ({ ...p, recorded_at: e.target.value }))} />
                {(() => {
                  const rec = latestVitals?.[activeFormMetric.primaryMetricType || vitalForm.metric_type] || latestVitals?.[vitalForm.metric_type];
                  const ts = rec?.recorded_at ?? rec?.updated_at;
                  if (!ts) return null;
                  return (
                    <div style={{ fontSize: '0.78rem', color: '#37a04b', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.7rem' }} />
                      Last recorded: {new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  );
                })()}
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:vitals.source')}</label>
                <div className="ec-category-chips">
                  {SOURCE_KEYS.map((sKey) => (
                    <button key={sKey} type="button" className={`ec-category-chip${vitalForm.source === sKey ? ' selected' : ''}`} onClick={() => setVitalForm((p) => ({ ...p, source: sKey }))}>
                      <FontAwesomeIcon icon={SOURCE_META[sKey].icon} />
                      {t(`elderly:vitals.${SOURCE_META[sKey].key}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:vitals.notes')}</label>
                <textarea rows={2} placeholder={t('elderly:vitals.notesPlaceholder')} value={vitalForm.notes} onChange={(e) => setVitalForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
          </div>
          <div className="ec-form-actions">
            <button className="ec-cancel-btn" onClick={() => setShowRecordVital(false)}>{t('elderly:vitals.cancelRecord')}</button>
            <button className="ec-submit-reminder-btn" onClick={handleVitalSubmit} disabled={vitalSubmitting}>
              <FontAwesomeIcon icon={faCheckCircle} /> {vitalSubmitting ? t('elderly:vitals.saving') : t('elderly:vitals.saveVital')}
            </button>
          </div>
        </div>
      )}

      {/* No data hint for dependants */}
      {selectedDependant && latestVitals && Object.keys(latestVitals).length === 0 && (
        <div className="ec-empty-vitals-banner">
          <FontAwesomeIcon icon={faInfoCircle} />
          <div>
            <strong>No vitals recorded for {elderlyMembers.find(m => m.id === selectedMember)?.name} yet.</strong>
            <p style={{ margin: '4px 0 0' }}>Common vitals (Heart Rate, BP, etc.) are fetched from <strong>Health Records</strong>. Click <strong>"Record Vital"</strong> to add elderly-specific metrics like Temperature, Sleep, Steps, etc.</p>
          </div>
        </div>
      )}

      {!alertsLoading && vitalAlerts.length > 0 && (
        <div style={{
          marginBottom: 20,
          borderRadius: 12,
          border: '1px solid #fecaca',
          background: 'linear-gradient(135deg, #fef2f2, #fff)',
          padding: '16px 18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h4 style={{ margin: 0, color: '#b91c1c', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Active Alerts ({vitalAlerts.length})
              {selectedDependant && (
                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b', marginLeft: 6 }}>
                  for {elderlyMembers.find(m => m.id === selectedMember)?.name}
                </span>
              )}
            </h4>
            {vitalAlerts.some((a: any) => !a.is_read) && (
              <button
                type="button"
                onClick={async () => {
                  await elderlyCareService.markAllAlertsRead(selectedDependant);
                  fetchVitalAlerts(selectedDependant);
                  fetchStatCards(selectedDependant);
                }}
                style={{ border: 0, background: 'transparent', color: '#0e7490', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Mark all read
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
            {vitalAlerts.map((alert: any) => {
              const sevColor = alert.severity === 'critical' ? '#dc2626' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6';
              return (
                <div key={alert.id} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#fff',
                  border: `1px solid ${sevColor}33`,
                  borderLeft: `4px solid ${sevColor}`,
                  opacity: alert.is_read ? 0.7 : 1,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: sevColor,
                        background: `${sevColor}15`,
                        padding: '2px 8px',
                        borderRadius: 10,
                      }}>{alert.severity}</span>
                      {alert.vital_record?.metric_type && (
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {alert.vital_record.metric_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: 'auto' }}>
                        {new Date(alert.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.4 }}>{cleanNum(alert.message)}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {!alert.is_read && (
                      <button
                        type="button"
                        onClick={() => handleMarkAlertRead(alert.id)}
                        style={{ border: 0, background: '#f1f5f9', color: '#475569', fontSize: '0.72rem', padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}
                      >Read</button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleResolveAlert(alert.id)}
                      style={{ border: 0, background: '#dcfce7', color: '#166534', fontSize: '0.72rem', padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}
                    >Resolve</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vital Metrics Table */}
      <div className="ec-vitals-table-wrapper">
        <div className="ec-table-heading">
          <h4><FontAwesomeIcon icon={faHeartbeat} /> {t('elderly:vitals.vitalMetric')}</h4>
          <span>{t('elderly:vitals.snapshotHint')}</span>
        </div>
        <table className="ec-vitals-table">
          <thead>
            <tr>
              <th>{t('elderly:vitals.vitalMetric')}</th>
              <th>{t('elderly:vitals.useCaseInElderHealth')}</th>
              <th>{t('elderly:vitals.latestValue')}</th>
              <th>{t('elderly:vitals.normalRange')}</th>
            </tr>
          </thead>
          <tbody>
            {vitalMetrics.map((metric) => (
              <tr key={metric.id}>
                <td>
                  <div className="ec-vital-name">
                    <div className="ec-vital-icon-sm" style={{ background: `${metric.color}15`, color: metric.color }}>
                      <FontAwesomeIcon icon={metric.icon} />
                    </div>
<span>{metric.name}</span>
                    {VITAL_HELP[metric.id] && <HelpTip text={VITAL_HELP[metric.id]} />}
                  </div>
                </td>
                <td className="ec-vital-usecase">{metric.useCase}</td>
                <td>
                  <span className="ec-vital-value" style={{ color: metric.color }}>
                    {getLatestValue(metric.id)}{' '}
                    <small style={{ color: '#94a3b8', fontWeight: 400 }}>{getMetricUnit(metric)}</small>
                  </span>
                </td>
                <td>
                  <span className="ec-vital-range">{metric.normalRange}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {/* Previously Recorded Vitals history */}
      <div className="ec-vitals-history">
        <div className="ec-vitals-history-header">
          <h4><FontAwesomeIcon icon={faClock} /> {t('elderly:vitals.recordedHistory')}</h4>
          {selectedDependant && (
            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#f59e0b' }}>
              for {elderlyMembers.find(m => m.id === selectedMember)?.name}
            </span>
          )}
        </div>
        {vitalHistoryLoading ? (
          <div className="ec-vitals-loading">
            <div className="ec-spinner" />
            <span>Loading history...</span>
          </div>
        ) : vitalHistory.length === 0 ? (
          <div className="ec-empty-vitals-banner" style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <div>
              <strong>No vitals recorded yet.</strong>
              <p style={{ margin: '4px 0 0' }}>Records you save through the <strong>Record Vital</strong> form will appear here with their recorded time.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="ec-vitals-history-list">
              {(showAllHistory ? vitalHistory : vitalHistory.slice(0, 15)).map((rec) => {
                const mt = rec.metric_type;
                const formM = formMetricConfig.find((m) => m.id === mt);
                const note = (rec.notes || '').trim();
                const src = SOURCE_META[rec.source];
                return (
                  <div key={rec.id} className="ec-vitals-history-row">
                    <div className="ec-vitals-history-line">
                      <div className="ec-vitals-history-metric">
                        <FontAwesomeIcon icon={formM?.icon || faHeartbeat} style={{ color: formM?.color || '#64748b' }} />
                        <span>{formM?.label || mt.replace(/_/g, ' ')}</span>
                      </div>
                      {src && (
                        <span className="ec-vitals-source-badge" style={{ background: src.bg, color: src.fg }}>
                          <FontAwesomeIcon icon={src.icon} />
                          {t(`elderly:vitals.${src.key}`)}
                        </span>
                      )}
                      <div className="ec-vitals-history-value">
                        {rec.display_value || `${rec.value}${rec.value_secondary != null ? '/' + rec.value_secondary : ''} ${rec.unit || ''}`.trim()}
                      </div>
                      <div className="ec-vitals-history-time">
                        {rec.recorded_at
                          ? new Date(rec.recorded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '--'}
                      </div>
                    </div>
                    {note && (
                      <p className="ec-vitals-history-notes">
                        <FontAwesomeIcon icon={faComments} />
                        <span>{note}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {vitalHistory.length > 15 && (
              <button
                type="button"
                className="ec-vitals-history-more"
                onClick={() => setShowAllHistory((v) => !v)}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.7rem' }} />
                {showAllHistory ? `Show less (latest 15)` : `Show all ${vitalHistory.length} records`}
              </button>
            )}
          </>
        )}
      </div>

      {vitalsLoading && (
        <div className="ec-vitals-loading">
          <div className="ec-spinner" />
          <span>{t('elderly:vitals.loadingVitals')}</span>
        </div>
      )}
    </div>
  );



  /* --- Render Trends Dashboard (built for non-clinical readers) --- */
  const renderComparison = () => {
    // Turn each selected metric into chart-ready lines. Blood pressure keeps
    // both numbers on one record (value / value_secondary), so its second line
    // is derived from the same series; every other pair is its own metric.
    const cards = selectedMetrics
      .map((metricId) => {
        const metric = vitalMetrics.find((m) => m.id === metricId);
        if (!metric) return null;
        const seriesMap = trendData[metricId] || {};
        const defs = metricChartMeta[metricId]?.series || [];
        const isBP = metricId === 'blood_pressure';
        const groups: PreparedSeries[][] = [];
        const stats: (SeriesSummary & { name: string; unit: string })[] = [];

        defs.forEach((def, i) => {
          const raw = seriesMap[def.key] || [];
          const points =
            isBP && i === 1
              ? raw
                  .filter((p) => p.valueSecondary != null)
                  .map((p) => ({ ...p, value: p.valueSecondary as number, valueSecondary: null }))
              : raw;
          const name = t(`elderly:vitals.${def.nameKey}`);
          const summary = summarizeSeries(points, def.band);
          if (!summary.total) return;
          const line: PreparedSeries = {
            key: `ec-s${i}`,
            name,
            unit: def.unit,
            color: def.tint === 'soft' ? '#7c3aed' : metric.color,
            band: def.band,
            data: fillDayGaps(bucketByDay(points)),
          };
          if (def.sharedAxis && groups.length) groups[groups.length - 1].push(line);
          else groups.push([line]);
          stats.push({ ...summary, name, unit: def.unit });
        });

        if (!stats.length) return { metric, metricId, groups, stats, status: 'none' as const };
        const status = stats.some((s) => s.status === 'alert')
          ? ('alert' as const)
          : stats.some((s) => s.status === 'watch')
            ? ('watch' as const)
            : ('normal' as const);
        return { metric, metricId, groups, stats, status };
      })
      .filter(Boolean) as {
        metric: VitalMetric;
        metricId: string;
        groups: PreparedSeries[][];
        stats: (SeriesSummary & { name: string; unit: string })[];
        status: keyof typeof TREND_STATUS_META;
      }[];

    const totalReadings = cards.reduce((s, c) => s + c.stats.reduce((n, st) => n + st.total, 0), 0);
    const totalOutside = cards.reduce((s, c) => s + c.stats.reduce((n, st) => n + st.outOfRange, 0), 0);
    const atLimit = selectedMetrics.length >= 4;

    return (
      <div className="ec-comparison">
        {/* What to look at + how far back */}
        <div className="ec-trends-controls">
          <div className="ec-trends-picker">
            <h4>
              <FontAwesomeIcon icon={faChartLine} /> {t('elderly:vitals.chooseMetrics')}
            </h4>
            <p>{t('elderly:vitals.chooseUpTo4')}</p>
            <div className="ec-metric-chips">
              {vitalMetrics.map((m) => {
                const active = selectedMetrics.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`ec-metric-chip${active ? ' active' : ''}`}
                    style={active ? { background: m.color, borderColor: m.color, color: '#fff' } : undefined}
                    disabled={!active && atLimit}
                    aria-pressed={active}
                    onClick={() => toggleMetricSelection(m.id)}
                  >
                    {active && <FontAwesomeIcon icon={faCheck} />}
                    <FontAwesomeIcon icon={m.icon} />
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ec-trends-period" role="group" aria-label={t('elderly:vitals.choosePeriod')}>
            <span className="ec-trends-period-label">{t('elderly:vitals.period')}</span>
            <div className="ec-trends-period-group">
              {TREND_PERIODS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  className={`ec-period-btn${trendDays === p.days ? ' active' : ''}`}
                  aria-pressed={trendDays === p.days}
                  onClick={() => setTrendDays(p.days)}
                >
                  {t(`elderly:vitals.${p.key}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* One plain sentence covering every selected reading */}
        {selectedMetrics.length > 0 && !trendLoading && totalReadings > 0 && (
          <div className={`ec-trends-summary${totalOutside ? ' warn' : ' good'}`}>
            <FontAwesomeIcon icon={totalOutside ? faExclamationTriangle : faCheckCircle} />
            <div>
              <strong>
                {totalOutside
                  ? t('elderly:vitals.summarySomeOutside', { n: totalOutside, total: totalReadings })
                  : t('elderly:vitals.summaryAllNormal', { total: totalReadings })}
              </strong>
              <span>{t('elderly:vitals.trendsSubtitle')}</span>
            </div>
          </div>
        )}

        {trendLoading ? (
          <div className="ec-trend-grid">
            {selectedMetrics.map((metricId) => (
              <div key={metricId} className="ec-trend-card skeleton" aria-hidden="true">
                <div className="ec-skel-line w40" />
                <div className="ec-skel-line w60 tall" />
                <div className="ec-skel-block" />
                <div className="ec-skel-line w30" />
              </div>
            ))}
          </div>
        ) : selectedMetrics.length === 0 ? (
          <div className="ec-comparison-empty">
            <FontAwesomeIcon icon={faChartLine} />
            <p>{t('elderly:vitals.chooseMetricPrompt')}</p>
            <button type="button" className="ec-trends-cta" onClick={openRecordVital}>
              <FontAwesomeIcon icon={faHeartbeat} /> {t('elderly:vitals.recordVitalCta')}
            </button>
          </div>
        ) : (
          <div className="ec-trend-grid">
            {cards.map(({ metric, metricId, groups, stats, status }) => {
              const badge = TREND_STATUS_META[status];
              const headline = stats[0];
              const trend = getTrendWord(groups[0]?.[0]?.data || [], metricId);
              const isBP = metricId === 'blood_pressure';
              const band = groups.flatMap((g) => g.map((s) => s.band).filter(Boolean)) as [number, number][];

              return (
                <div key={metricId} className="ec-trend-card">
                  <div className="ec-trend-card-strip" style={{ background: `linear-gradient(90deg, ${metric.color}, ${metric.color}33)` }} />

                  <div className="ec-trend-card-header">
                    <div className="ec-trend-card-title">
                      <span className="ec-vital-icon-sm" style={{ background: `${metric.color}15`, color: metric.color }}>
                        <FontAwesomeIcon icon={metric.icon} />
                      </span>
                      <h4>{metric.name}</h4>
                    </div>
                    {status !== 'none' && (
                      <span className="ec-trend-status" style={{ background: badge.bg, color: badge.fg }}>
                        <FontAwesomeIcon icon={badge.icon} />
                        {t(`elderly:vitals.${badge.key}`)}
                      </span>
                    )}
                  </div>
                  {headline ? (
                    <>
                      <div className="ec-trend-hero">
                        <div className="ec-trend-hero-main">
                          <span className="ec-trend-hero-value" style={{ color: metric.color }}>
                            {fmtNum(headline.latest as number)}
                          </span>
                          <span className="ec-trend-hero-unit">{headline.unit}</span>
                        </div>
                        {isBP && stats[1] && (
                          <div className="ec-trend-hero-main">
                            <span className="ec-trend-hero-value second">{fmtNum(stats[1].latest as number)}</span>
                            <span className="ec-trend-hero-unit">{stats[1].unit}</span>
                          </div>
                        )}
                        <div className="ec-trend-hero-meta">
                          <span className="ec-trend-trend" style={{ color: trend.color }}>
                            <FontAwesomeIcon icon={trend.icon} /> {t(`elderly:vitals.${trend.key}`)}
                          </span>
                          {headline.latestDate && (
                            <span className="ec-trend-when">{t('elderly:vitals.latestOn', { date: headline.latestDate })}</span>
                          )}
                        </div>
                        {isBP && (
                          <span className="ec-trend-hint">{t('elderly:vitals.topBottomHint')}</span>
                        )}
                      </div>

                      <div className="ec-trend-charts">
                        {groups.map((group) => renderTrendChart(group))}
                      </div>

                      <div className="ec-trend-stats">
                        {stats.map((s) => (
                          <div key={s.name} className="ec-trend-stat-row">
                            <span className="ec-trend-stat-name">{s.name}</span>
                            <span className="ec-trend-stat-nums">
                              <span>
                                <em>{t('elderly:vitals.average')}</em> {fmtNum(s.avg as number)} {s.unit}
                              </span>
                              <span>
                                <em>{t('elderly:vitals.lowest')}</em> {fmtNum(s.low as number)} {s.unit}
                              </span>
                              <span>
                                <em>{t('elderly:vitals.highest')}</em> {fmtNum(s.high as number)} {s.unit}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="ec-trend-card-foot">
                        {band.map((b, i) => (
                          <span key={i} className="ec-trend-foot-item">
                            <span className="ec-trend-band-swatch" />
                            {t('elderly:vitals.usualRangeIs', { range: `${fmtNum(b[0])}–${fmtNum(b[1])}`, unit: stats[i]?.unit || '' })}
                          </span>
                        ))}
                        <span className="ec-trend-foot-item muted">
                          {t('elderly:vitals.readingsCount', { n: stats.reduce((n, s) => n + s.total, 0) })}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="ec-trend-nodata">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <p>{t('elderly:vitals.noReadingsYet', { days: trendDays })}</p>
                      <button type="button" className="ec-trends-cta" onClick={openRecordVital}>
                        <FontAwesomeIcon icon={faHeartbeat} /> {t('elderly:vitals.recordVitalCta')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  /* --- Render Digital Features --- */
  /* --- DISABLED (see Digital Features note above) ---
  const renderDigitalFeatures = () => (
    <div className="ec-digital-features">
      <div className="ec-digital-features-grid">
        {(selectedDependant
          ? digitalFeatures.filter((f) => f.action === 'reminders' || f.action === 'analytics')
          : digitalFeatures
        ).map((feature, idx) => {
          const status = digitalStatus[feature.action];
          return (
            <div
              key={idx}
              className="ec-digital-card ec-digital-card-actionable"
              style={{ borderLeft: `4px solid ${feature.color}` }}
onClick={() => {
                  if (feature.action === 'reminders') {
                    setActiveTab('reminders');
                  } else if (feature.action === 'analytics' && selectedDependant) {
                    setVitalsSubTab('comparison');
                  } else {
                    navigate(feature.link);
                  }
                }}
            >
              <div className="ec-digital-card-icon" style={{ background: `${feature.color}12`, color: feature.color }}>
                <FontAwesomeIcon icon={feature.icon} />
              </div>
              <div className="ec-digital-card-content">
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
                { Status }
                <div className="ec-digital-status">
                  <span className={`ec-digital-status-dot${status?.active ? ' active' : ''}`} />
                  <span className="ec-digital-status-text">{status?.detail || 'Checking...'}</span>
                </div>
              </div>
              <div className="ec-digital-card-arrow" style={{ color: feature.color }}>
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
  */

  /* --- Render Remote Health Monitoring Process Flow --- */
  /* --- DISABLED (see Remote Monitoring note above) ---
  const renderRemoteMonitoring = () => {
    const v = monitoringData.vitals;
    let stepNum = 0;
    const step = () => ++stepNum;
    const liveVitals = [
      { label: 'Heart Rate', value: v?.heartRate?.value ?? v?.heart_rate?.value ?? '--', unit: 'bpm', icon: faHeartbeat, color: '#ef4444', normal: '60-100' },
      { label: 'Blood Pressure', value: v?.bloodPressure ? `${v.bloodPressure.systolic}/${v.bloodPressure.diastolic}` : v?.blood_pressure ? `${v.blood_pressure.value}${v.blood_pressure.value_secondary != null ? `/${v.blood_pressure.value_secondary}` : ''}` : '--', unit: 'mmHg', icon: faTint, color: '#3b82f6', normal: '<120/80' },
      { label: 'SpO2', value: v?.oxygenSaturation?.value ?? v?.spo2?.value ?? '--', unit: '%', icon: faLungs, color: '#06b6d4', normal: '95-100' },
      { label: 'Blood Sugar', value: v?.glucose?.value ?? v?.blood_sugar?.value ?? '--', unit: 'mg/dL', icon: faTint, color: '#f59e0b', normal: '70-100' },
      { label: 'BMI', value: v?.bmi?.value ?? '--', unit: '', icon: faWeight, color: '#10b981', normal: '18.5-24.9' },
    ];

    return (
      <div className="ec-remote-monitoring-live">
        {monitoringLoading && (
          <div className="ec-vitals-loading"><div className="ec-spinner" /><span>Loading live data...</span></div>
        )}

        { STEP 1: Scheduled Vitals Tracking }
        <div className="ec-rm-step">
          <div className="ec-rm-step-header">
            <div className="ec-rm-step-num" style={{ background: '#0e7490' }}>{step()}</div>
            <div className="ec-rm-step-title">
              <h3><FontAwesomeIcon icon={faBell} /> Scheduled Vitals Tracking</h3>
              <p>Automated reminders for checking vitals. Users record through app or connected devices.</p>
            </div>
            <button className="ec-rm-step-action" style={{ background: '#0e7490' }} onClick={() => setActiveTab('reminders')}>
              <FontAwesomeIcon icon={faBell} /> Add Reminder
            </button>
          </div>
          <div className="ec-rm-step-body">
            {monitoringData.upcomingReminders.length > 0 ? (
              <div className="ec-upcoming-reminders">
                {monitoringData.upcomingReminders.map((rem: any) => {
                  const catMeta = getCategoryMeta(rem.category);
                  const isOverdue = new Date(rem.reminder_datetime) < new Date();
                  return (
                    <div key={rem.id} className="ec-upcoming-rem-card" style={isOverdue ? { borderLeft: '3px solid #ef4444' } : {}}>
                      <div className="ec-upcoming-rem-icon" style={{ background: `${catMeta.color}12`, color: catMeta.color }}>
                        <FontAwesomeIcon icon={catMeta.icon} />
                      </div>
                      <div className="ec-upcoming-rem-info">
                        <strong>{rem.title}</strong>
                        <span><FontAwesomeIcon icon={faClock} /> {new Date(rem.reminder_datetime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {isOverdue && <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: 6 }}>Overdue</span>}
                        </span>
                      </div>
                      <span className="ec-upcoming-rem-cat" style={{ background: `${catMeta.color}15`, color: catMeta.color }}>{catMeta.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ec-rm-empty">
                <FontAwesomeIcon icon={faBell} /> No scheduled reminders. Set up vitals tracking reminders to get started.
              </div>
            )}
          </div>
        </div>

        { STEP 2: Auto-Sync via IoT/Wearables — hidden in elder mode (device sync is self/account-bound) }
        {!selectedDependant && (
          <div className="ec-rm-step">
          <div className="ec-rm-step-header">
            <div className="ec-rm-step-num" style={{ background: '#8b5cf6' }}>{step()}</div>
            <div className="ec-rm-step-title">
              <h3><FontAwesomeIcon icon={faWifi} /> Auto-Sync via IoT/Wearables</h3>
              <p>Data from approved devices captured in real time and automatically synced.</p>
            </div>
            <button className="ec-rm-step-action" style={{ background: '#8b5cf6' }} onClick={() => navigate('/wearable-devices')}>
              <FontAwesomeIcon icon={faSync} /> Manage Devices
            </button>
          </div>
          <div className="ec-rm-step-body">
            {monitoringData.wearableData.length > 0 ? (
              <div className="ec-wearable-readings">
                {monitoringData.wearableData.slice(0, 6).map((dp: any, idx: number) => (
                  <div key={idx} className="ec-wearable-reading-card">
                    <div className="ec-wearable-reading-type">{(dp.metric_type || dp.type || 'metric').replace(/_/g, ' ')}</div>
                    <div className="ec-wearable-reading-value">{dp.value ?? '--'}</div>
                    <div className="ec-wearable-reading-time">
                      {dp.recorded_at ? new Date(dp.recorded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '--'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ec-rm-empty">
                <FontAwesomeIcon icon={faWifi} /> No wearable data synced yet. Connect a device to start auto-syncing.
              </div>
            )}
          </div>
        </div>
        )}

        { STEP 3: Health Trend Analysis & Alerts }
        <div className="ec-rm-step">
          <div className="ec-rm-step-header">
            <div className="ec-rm-step-num" style={{ background: '#ef4444' }}>{step()}</div>
            <div className="ec-rm-step-title">
              <h3><FontAwesomeIcon icon={faChartBar} /> Health Trend Analysis & Alerts</h3>
              <p>System analyzes data for trends and deviations. Abnormal readings trigger instant alerts.</p>
            </div>
            <button className="ec-rm-step-action" style={{ background: '#ef4444' }} onClick={() => selectedDependant ? setVitalsSubTab('comparison') : navigate('/health-records')}>
              <FontAwesomeIcon icon={faChartLine} /> View Trends
            </button>
          </div>
          <div className="ec-rm-step-body">
            { Live Vitals }
            <div className="ec-live-vitals-grid">
              {liveVitals.map((vital, idx) => (
                <div key={idx} className="ec-live-vital-card">
                  <div className="ec-live-vital-icon" style={{ background: `${vital.color}12`, color: vital.color }}>
                    <FontAwesomeIcon icon={vital.icon} />
                  </div>
                  <div className="ec-live-vital-data">
                    <span className="ec-live-vital-label">{vital.label}</span>
                    <span className="ec-live-vital-value" style={{ color: vital.color }}>
                      {vital.value} <small>{vital.unit}</small>
                    </span>
                    <span className="ec-live-vital-normal">Normal: {vital.normal}</span>
                  </div>
                </div>
              ))}
            </div>
            { Anomaly Alerts }
            {monitoringData.alerts.length > 0 && (
              <div className="ec-alerts-list" style={{ marginTop: 16 }}>
                {monitoringData.alerts.map((alert, idx) => (
                  <div key={idx} className="ec-alert-card" style={{ borderLeft: `4px solid ${alert.color}` }}>
                    <div className="ec-alert-icon" style={{ background: `${alert.color}12`, color: alert.color }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="ec-alert-content">
                      <div className="ec-alert-header">
                        <strong>{alert.metric}</strong>
                        <span className="ec-alert-value" style={{ color: alert.color }}>{alert.value}</span>
                      </div>
                      <p>{alert.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {monitoringData.alerts.length === 0 && v && (
              <div className="ec-rm-ok" style={{ marginTop: 12 }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} /> All vitals within normal range. No anomalies detected.
              </div>
            )}
          </div>
        </div>

        { STEP 4: Family Dashboard (self) / Family Dashboard & Elder Monitoring (elder) }
        <div className="ec-rm-step">
          <div className="ec-rm-step-header">
            <div className="ec-rm-step-num" style={{ background: '#f59e0b' }}>{step()}</div>
            <div className="ec-rm-step-title">
              <h3><FontAwesomeIcon icon={faUserFriends} /> {selectedDependant ? 'Family Dashboard & Elder Monitoring' : 'Family Dashboard & Wellness Reports'}</h3>
              <p>{selectedDependant ? 'Family-facing dashboard with the elder\'s daily vitals, trends and alerts.' : 'Shared dashboard with daily vitals, trends, goals and alerts. Periodic wellness reports.'}</p>
            </div>
            {!selectedDependant && (
              <button className="ec-rm-step-action" style={{ background: '#f59e0b' }} onClick={() => navigate('/health-summary')}>
                <FontAwesomeIcon icon={faChartBar} /> View Reports
              </button>
            )}
          </div>
          <div className="ec-rm-step-body">
            <div className="ec-rm-family-summary">
              <div className="ec-rm-family-card">
                <FontAwesomeIcon icon={faHeartbeat} style={{ color: '#ef4444' }} />
                <div>
                  <strong>Latest Vitals</strong>
                  <span>{monitoringData.stats?.vitals_tracked ? `${monitoringData.stats.vitals_tracked} vitals tracked` : v ? 'Data available' : 'No data yet'}</span>
                </div>
              </div>
              <div className="ec-rm-family-card">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: monitoringData.alerts.length > 0 ? '#ef4444' : '#10b981' }} />
                <div>
                  <strong>Alerts</strong>
                  <span>{monitoringData.alerts.length > 0 ? `${monitoringData.alerts.length} anomalies detected` : 'All normal'}</span>
                </div>
              </div>
              <div className="ec-rm-family-card">
                <FontAwesomeIcon icon={faBell} style={{ color: '#0e7490' }} />
                <div>
                  <strong>Reminders</strong>
                  <span>{monitoringData.upcomingReminders.length} upcoming</span>
                </div>
              </div>
              {!selectedDependant && (
              <div className="ec-rm-family-card">
                <FontAwesomeIcon icon={faSync} style={{ color: '#8b5cf6' }} />
                <div>
                  <strong>Device Sync</strong>
                  <span>{monitoringData.wearableData.length > 0 ? `${monitoringData.wearableData.length} readings` : 'No devices'}</span>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        { STEP 5: AI-Based Predictive Health Scoring — hidden in elder mode (no elder-scoped AI score) }
        {!selectedDependant && (
        <div className="ec-rm-step">
          <div className="ec-rm-step-header">
            <div className="ec-rm-step-num" style={{ background: '#6366f1' }}>{step()}</div>
            <div className="ec-rm-step-title">
              <h3><FontAwesomeIcon icon={faRobot} /> AI-Based Predictive Health Scoring</h3>
              <p>AI models assess combined vitals, lifestyle data and historical patterns for early risk detection.</p>
            </div>
            <button className="ec-rm-step-action" style={{ background: '#6366f1' }} onClick={() => navigate('/disease-prediction')}>
              <FontAwesomeIcon icon={faRobot} /> Run Prediction
            </button>
          </div>
          <div className="ec-rm-step-body">
            {healthScoreData.risk_score !== null || healthScoreData.health_score !== null ? (
              <div className="ec-live-vitals-grid">
                {healthScoreData.health_score !== null && (
                  <div className="ec-live-vital-card">
                    <div className="ec-live-vital-icon" style={{ background: '#10b98112', color: '#10b981' }}>
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="ec-live-vital-data">
                      <span className="ec-live-vital-label">Health Score</span>
                      <span className="ec-live-vital-value" style={{ color: '#10b981' }}>
                        {healthScoreData.health_score}<small>/100</small>
                      </span>
                    </div>
                  </div>
                )}
                {healthScoreData.risk_score !== null && (
                  <div className="ec-live-vital-card">
                    <div className="ec-live-vital-icon" style={{ background: healthScoreData.risk_score > 60 ? '#ef444412' : '#10b98112', color: healthScoreData.risk_score > 60 ? '#ef4444' : '#10b981' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="ec-live-vital-data">
                      <span className="ec-live-vital-label">Risk Score</span>
                      <span className="ec-live-vital-value" style={{ color: healthScoreData.risk_score > 60 ? '#ef4444' : '#10b981' }}>
                        {healthScoreData.risk_score}<small>/100</small>
                      </span>
                    </div>
                  </div>
                )}
                {healthScoreData.risk_category && (
                  <div className="ec-live-vital-card">
                    <div className="ec-live-vital-icon" style={{ background: '#6366f112', color: '#6366f1' }}>
                      <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <div className="ec-live-vital-data">
                      <span className="ec-live-vital-label">Risk Category</span>
                      <span className="ec-live-vital-value" style={{ color: '#6366f1' }}>
                        {healthScoreData.risk_category}
                      </span>
                    </div>
                  </div>
                )}
                {healthScoreData.generated_at && (
                  <div className="ec-live-vital-card">
                    <div className="ec-live-vital-icon" style={{ background: '#f59e0b12', color: '#f59e0b' }}>
                      <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className="ec-live-vital-data">
                      <span className="ec-live-vital-label">Last Generated</span>
                      <span className="ec-live-vital-value" style={{ color: '#f59e0b', fontSize: '0.95rem' }}>
                        {new Date(healthScoreData.generated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="ec-rm-ai-cta">
                <div className="ec-rm-ai-icon"><FontAwesomeIcon icon={faRobot} /></div>
                <div className="ec-rm-ai-text">
                  <h4>No Health Score Yet</h4>
                  <p>Generate an AI-powered health score based on your vitals, lifestyle data, and medical history to identify early risks and get proactive care recommendations.</p>
                </div>
                <button className="ec-rm-step-action" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }} onClick={() => navigate('/disease-prediction')}>
                  <FontAwesomeIcon icon={faArrowRight} /> Get Score
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    );
  };
  */

  /* --- Render Reminders Tab --- */
  /* --- Reminder handlers --- */
  const handleReminderSubmit = async () => {
    if (!reminderForm.title.trim() || !reminderForm.reminder_datetime) {
      setToastMsg({ message: t('elderly:remindersTab.titleRequired'), type: 'info' });
      setTimeout(() => setToastMsg(null), 3000);
      return;
    }
    setReminderSubmitting(true);
    try {
      const payload = { ...reminderForm, dependant: selectedDependant ?? null };
      if (editingReminderId) {
        await reminderService.updateReminder(editingReminderId, payload);
        setToastMsg({ message: t('elderly:remindersTab.reminderUpdated'), type: 'success' });
      } else {
        await reminderService.createReminder(payload);
        setToastMsg({ message: t('elderly:remindersTab.reminderAdded'), type: 'success' });
      }
      setReminderForm(defaultReminderForm);
      setEditingReminderId(null);
      setShowAddReminder(false);
      setTimeout(() => setToastMsg(null), 3000);
      fetchReminders(selectedMember ?? undefined);
      fetchStatCards(selectedMember ?? undefined);
    } catch {
      setToastMsg({ message: editingReminderId ? t('elderly:remindersTab.updateFailed') : t('elderly:remindersTab.addFailed'), type: 'info' });
      setTimeout(() => setToastMsg(null), 3000);
    } finally {
      setReminderSubmitting(false);
    }
  };

  const handleEditReminder = (rem: Reminder) => {
    setReminderForm({
      title: rem.title,
      description: rem.description || '',
      reminder_datetime: rem.reminder_datetime ? rem.reminder_datetime.slice(0, 16) : '',
      category: rem.category || 'medication',
      priority: rem.priority || 'medium',
      recurrence: rem.recurrence || 'one_time',
      notes: rem.notes || '',
    });
    setEditingReminderId(rem.id);
    setShowAddReminder(true);
  };

  const handleMarkComplete = async (id: number) => {
    try {
      await reminderService.markComplete(id);
      fetchReminders(selectedMember ?? undefined);
      fetchStatCards(selectedMember ?? undefined);
    } catch { /* ignore */ }
  };

  const handleDeleteReminder = async (id: number) => {
    try {
      await reminderService.deleteReminder(id);
      fetchReminders(selectedMember ?? undefined);
      fetchStatCards(selectedMember ?? undefined);
    } catch { /* ignore */ }
  };

  const getCategoryMeta = (cat: string) => {
    const map: Record<string, { icon: any; color: string; label: string }> = {
      medication: { icon: faPills, color: '#ef4444', label: 'Medication' },
      appointment: { icon: faCalendarAlt, color: '#3b82f6', label: 'Appointment' },
      lab_test: { icon: faVial, color: '#06b6d4', label: 'Lab Test' },
      follow_up: { icon: faClipboardCheck, color: '#f59e0b', label: 'Follow-Up' },
      vaccination: { icon: faSyringe, color: '#8b5cf6', label: 'Vaccination' },
      insurance: { icon: faShieldAlt, color: '#ec4899', label: 'Insurance Renewal' },
      health_checkup: { icon: faStethoscope, color: '#10b981', label: 'Health Check-Up' },
      personal: { icon: faBell, color: '#64748b', label: 'Personal' },
      task: { icon: faClipboardCheck, color: '#6366f1', label: 'Task' },
      other: { icon: faBell, color: '#94a3b8', label: 'Other' },
    };
    return map[cat] || map.other;
  };

  const getPriorityColor = (p: string) => {
    if (p === 'high') return '#ef4444';
    if (p === 'medium') return '#f59e0b';
    return '#10b981';
  };

  const renderReminders = () => (
    <div className="ec-reminders-live">
      {/* Add Reminder Button */}
      <div className="ec-reminder-actions">
        <button className="ec-add-reminder-btn" onClick={() => {
          if (showAddReminder) {
            setShowAddReminder(false);
            setEditingReminderId(null);
            setReminderForm(defaultReminderForm);
          } else {
            setEditingReminderId(null);
            setReminderForm(defaultReminderForm);
            setShowAddReminder(true);
          }
        }}>
          <FontAwesomeIcon icon={showAddReminder ? faTimesCircle : faBell} />
          {showAddReminder ? t('elderly:remindersTab.cancelReminder') : t('elderly:remindersTab.addReminder')}
        </button>
      </div>

      {/* Add Reminder Form - Medicine Reminder Style */}
      {showAddReminder && (
        <div className="ec-add-reminder-form">
          <h4><FontAwesomeIcon icon={faBell} /> {editingReminderId ? t('elderly:remindersTab.editReminder') : t('elderly:remindersTab.newReminder')}</h4>

          {/* Category Chips */}
          <div className="ec-form-group" style={{ marginBottom: 22 }}>
            <label>{t('elderly:remindersTab.category')}</label>
            <div className="ec-category-chips">
              {[
                { value: 'medication', label: 'Medication', icon: faPills },
                { value: 'appointment', label: 'Appointment', icon: faCalendarAlt },
                { value: 'lab_test', label: 'Lab Test', icon: faVial },
                { value: 'follow_up', label: 'Follow-Up', icon: faClipboardCheck },
                { value: 'vaccination', label: 'Vaccination', icon: faSyringe },
                { value: 'insurance', label: 'Insurance Renewal', icon: faShieldAlt },
                { value: 'health_checkup', label: 'Health Check-Up', icon: faStethoscope },
              ].map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  className={`ec-category-chip${reminderForm.category === cat.value ? ' selected' : ''}`}
                  onClick={() => {
                    const defaults: Record<string, { recurrence: string; priority: string }> = {
                      medication: { recurrence: 'daily', priority: 'high' },
                      appointment: { recurrence: 'one_time', priority: 'high' },
                      lab_test: { recurrence: 'one_time', priority: 'medium' },
                      follow_up: { recurrence: 'one_time', priority: 'medium' },
                      vaccination: { recurrence: 'one_time', priority: 'high' },
                      insurance: { recurrence: 'one_time', priority: 'medium' },
                      health_checkup: { recurrence: 'monthly', priority: 'medium' },
                    };
                    const d = defaults[cat.value] || {};
                    setReminderForm((p) => ({ ...p, category: cat.value, ...d }));
                  }}
                >
                  <FontAwesomeIcon icon={cat.icon} /> {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ec-form-grid">
            {/* Left Column - Basic Details */}
            <div className="ec-form-column">
              <div className="ec-form-column-title">
                <FontAwesomeIcon icon={faClipboardCheck} /> {t('elderly:remindersTab.basicDetails')}
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:remindersTab.title')} <span className="ec-form-required">*</span></label>
                <input
                  type="text"
                  placeholder={
                    reminderForm.category === 'medication' ? 'e.g. Take BP medication, Insulin dose' :
                    reminderForm.category === 'appointment' ? 'e.g. Dr. Sharma consultation, Eye check-up' :
                    reminderForm.category === 'lab_test' ? 'e.g. Blood test report, HbA1c review' :
                    reminderForm.category === 'follow_up' ? 'e.g. Post-surgery follow-up, Diet review' :
                    reminderForm.category === 'vaccination' ? 'e.g. Flu vaccine, COVID booster' :
                    reminderForm.category === 'insurance' ? 'e.g. Star Health renewal, Mediclaim premium' :
                    reminderForm.category === 'health_checkup' ? 'e.g. Annual full-body check-up, Eye test' :
                    'e.g. Take BP medication, Doctor visit'
                  }
                  value={reminderForm.title}
                  onChange={(e) => setReminderForm((p) => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="ec-form-group">
                <label>{reminderForm.category === 'insurance' ? t('elderly:remindersTab.renewalDate') : t('elderly:remindersTab.dateTime')} <span className="ec-form-required">*</span></label>
                <input
                  type="datetime-local"
                  value={reminderForm.reminder_datetime}
                  onChange={(e) => setReminderForm((p) => ({ ...p, reminder_datetime: e.target.value }))}
                />
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:remindersTab.description')}</label>
                <textarea
                  placeholder={
                    reminderForm.category === 'medication' ? 'Dosage, timing instructions, doctor name...' :
                    reminderForm.category === 'appointment' ? 'Doctor name, clinic address, purpose of visit...' :
                    reminderForm.category === 'lab_test' ? 'Test name, lab location, fasting requirements...' :
                    reminderForm.category === 'follow_up' ? 'Follow-up reason, previous visit details...' :
                    reminderForm.category === 'vaccination' ? 'Vaccine name, dose number, hospital/clinic...' :
                    reminderForm.category === 'insurance' ? 'Policy number, provider, premium amount...' :
                    reminderForm.category === 'health_checkup' ? 'Check-up type, hospital, packages selected...' :
                    'Additional details...'
                  }
                  rows={3}
                  value={reminderForm.description}
                  onChange={(e) => setReminderForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Right Column - Settings */}
            <div className="ec-form-column">
              <div className="ec-form-column-title">
                <FontAwesomeIcon icon={faClock} /> {t('elderly:remindersTab.schedulePriority')}
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:remindersTab.priority')}</label>
                <div className="ec-category-chips">
                  {[
                    { value: 'low', label: 'Low', color: '#10b981' },
                    { value: 'medium', label: 'Medium', color: '#f59e0b' },
                    { value: 'high', label: 'High', color: '#ef4444' },
                  ].map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={`ec-category-chip${reminderForm.priority === p.value ? ' selected' : ''}`}
                      style={reminderForm.priority === p.value ? { borderColor: p.color, color: p.color, background: `${p.color}08` } : {}}
                      onClick={() => setReminderForm((prev) => ({ ...prev, priority: p.value }))}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} /> {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:remindersTab.recurrence')}</label>
                <div className="ec-category-chips">
                  {[
                    { value: 'one_time', label: 'One Time' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                  ].map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      className={`ec-category-chip${reminderForm.recurrence === r.value ? ' selected' : ''}`}
                      onClick={() => setReminderForm((prev) => ({ ...prev, recurrence: r.value }))}
                    >
                      <FontAwesomeIcon icon={faSync} /> {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ec-form-group">
                <label>{t('elderly:remindersTab.notes')}</label>
                <textarea
                  placeholder={
                    reminderForm.category === 'medication' ? 'Missed dose instructions, refill date, pharmacy details...' :
                    reminderForm.category === 'appointment' ? 'Bring previous reports, questions for doctor...' :
                    reminderForm.category === 'lab_test' ? 'Fasting required, bring ID, previous results...' :
                    reminderForm.category === 'vaccination' ? 'Side effects to watch, next dose schedule...' :
                    reminderForm.category === 'insurance' ? 'Claim process, agent contact, documents needed...' :
                    reminderForm.category === 'health_checkup' ? 'Fasting hours, packages, reports to carry...' :
                    'Any special instructions...'
                  }
                  rows={2}
                  value={reminderForm.notes || ''}
                  onChange={(e) => setReminderForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="ec-form-actions">
            <button className="ec-cancel-btn" onClick={() => { setShowAddReminder(false); setEditingReminderId(null); setReminderForm(defaultReminderForm); }}>
              Cancel
            </button>
            <button className="ec-submit-reminder-btn" onClick={handleReminderSubmit} disabled={reminderSubmitting}>
              <FontAwesomeIcon icon={faCheckCircle} />
              {reminderSubmitting ? t('elderly:vitals.saving') : editingReminderId ? t('elderly:remindersTab.updateReminder') : t('elderly:remindersTab.addReminder')}
            </button>
          </div>
        </div>
      )}

      {/* Reminders List */}
      {remindersLoading ? (
        <div className="ec-vitals-loading">
          <div className="ec-spinner" />
          <span>{t('elderly:remindersTab.loadingReminders')}</span>
        </div>
      ) : reminders.length === 0 ? (
        <div className="ec-comparison-empty">
          <FontAwesomeIcon icon={faBell} />
          <p>{t('elderly:remindersTab.noReminders')}</p>
        </div>
      ) : (
        <div className="ec-reminders-list">
          {reminders.map((rem) => {
            const catMeta = getCategoryMeta(rem.category);
            const dt = new Date(rem.reminder_datetime);
            const isOverdue = !rem.is_completed && dt < new Date();
            return (
              <div key={rem.id} className={`ec-reminder-item${rem.is_completed ? ' completed' : ''}${isOverdue ? ' overdue' : ''}`}>
                <div className="ec-reminder-item-left">
                  <div className="ec-reminder-item-icon" style={{ background: `${catMeta.color}12`, color: catMeta.color }}>
                    <FontAwesomeIcon icon={catMeta.icon} />
                  </div>
                  <div className="ec-reminder-item-info">
                    <span className="ec-reminder-item-title">{rem.title}</span>
                    <div className="ec-reminder-item-meta">
                      <span className="ec-reminder-item-cat" style={{ background: `${catMeta.color}15`, color: catMeta.color }}>{catMeta.label}</span>
                      <span className="ec-reminder-item-priority" style={{ color: getPriorityColor(rem.priority) }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {rem.priority}
                      </span>
                      <span className="ec-reminder-item-time">
                        <FontAwesomeIcon icon={faClock} /> {dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} {dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {rem.recurrence !== 'one_time' && (
                        <span className="ec-reminder-item-recur">
                          <FontAwesomeIcon icon={faSync} /> {rem.recurrence}
                        </span>
                      )}
                    </div>
                    {rem.description && <p className="ec-reminder-item-desc">{rem.description}</p>}
                  </div>
                </div>
                <div className="ec-reminder-item-actions">
                  {!rem.is_completed && (
                    <>
                      <button className="ec-rem-action-btn ec-rem-edit" onClick={() => handleEditReminder(rem)} title="Edit" style={{ color: '#3b82f6' }}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="ec-rem-action-btn ec-rem-complete" onClick={() => handleMarkComplete(rem.id)} title="Mark Complete">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </button>
                    </>
                  )}
                  <button className="ec-rem-action-btn ec-rem-delete" onClick={() => handleDeleteReminder(rem.id)} title="Delete">
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  /* --- Enquire Now handlers --- */
  const openEnquiry = (service: ServiceCardItem) => {
    setEnquiringService(service);
    setEnquiryForm({
      dependant: elderlyMembers.find((m) => m.id === selectedMember)?.id ?? null,
      name: user?.name || '',
      phone: user?.mobile_number || '',
      email: user?.email || '',
      preferred_time: '',
      message: '',
    });
    setEnquiryError(null);
    setEnquiryOpen(true);
  };

  const submitEnquiry = async () => {
    const svc = enquiringService;
    if (elderlyMembers.length === 0) { setEnquiryError(t('elderly:enquiry.noElders')); return; }
    if (!enquiryForm.dependant) { setEnquiryError(t('elderly:enquiry.elderRequired')); return; }
    if (!enquiryForm.name.trim()) { setEnquiryError(t('elderly:enquiry.nameRequired')); return; }
    if (!enquiryForm.phone.trim()) { setEnquiryError(t('elderly:enquiry.phoneRequired')); return; }
    setEnquirySubmitting(true);
    setEnquiryError(null);
    try {
      if (svc?.id) {
        await elderlyCareService.createEnquiry({
          service: svc.id,
          dependant: enquiryForm.dependant,
          name: enquiryForm.name.trim(),
          phone: enquiryForm.phone.trim(),
          email: enquiryForm.email.trim() || undefined,
          message: enquiryForm.message.trim() || undefined,
          preferred_time: enquiryForm.preferred_time.trim() || undefined,
        });
      }
      setToastMsg({ message: t('elderly:enquireToast', { service: svc?.title || '' }), type: 'success' });
      setTimeout(() => setToastMsg(null), 4000);
      setEnquiryOpen(false);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        (data && (data.name?.[0] || data.phone?.[0] || data.detail)) ||
        t('elderly:enquiry.failed');
      setEnquiryError(msg);
    } finally {
      setEnquirySubmitting(false);
    }
  };

  /* --- Render a grid of service cards with category accent color --- */
  const renderServiceCards = (
    services: ServiceCardItem[],
    accentColor: string,
    lightColor: string,
  ) => (
    <div className="ec-services-grid">
      {services.map((service, idx) => (
        <div
          className="ec-service-card"
          key={idx}
          style={{ borderTop: `4px solid ${accentColor}` }}
        >
          <div
            className="ec-service-icon"
            style={{ background: `linear-gradient(135deg, ${lightColor}, ${accentColor}15)` }}
          >
            <FontAwesomeIcon icon={service.icon} style={{ color: accentColor }} />
          </div>
          <h3 className="ec-service-title">{service.title}</h3>
          <p className="ec-service-desc">{service.description}</p>
          <button
            className="ec-enquire-btn"
            style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
            onClick={() => openEnquiry(service)}
          >
            {t('elderly:enquireNow')}
          </button>
        </div>
      ))}
    </div>
  );

  /* --- Derived data for active tab --- */
  const dynamicTabs = useMemo(() => {
    const dynamic = categories
      .filter((c) => c.slug !== 'vitals' && c.slug !== 'reminders')
      .map((c) => ({
        id: c.slug,
        label: c.name,
        icon: resolveEcIcon(c.icon),
        color: c.accent_color || '#334155',
        lightColor: c.light_color || '#f1f5f9',
        image: c.image || '',
      }));
    return [...FIXED_TABS, ...dynamic];
  }, [categories]);

  const activeTabData = dynamicTabs.find((t) => t.id === activeTab)!;
  const activeCategory = categories.find((c) => c.slug === activeTab);
  const activeDesc = activeCategory
    ? {
        title: activeCategory.description_title || activeCategory.name,
        subtitle: activeCategory.description_subtitle || '',
      }
    : (tabDescriptions[activeTab] || { title: activeTabData.label, subtitle: '' });

  const activeServices = useMemo<ServiceCardItem[]>(() => {
    if (!activeCategory?.services?.length) return [];
    return activeCategory.services.map((s) => ({
      id: s.id,
      title: s.name,
      description: s.description,
      icon: resolveEcIcon(s.icon),
    }));
  }, [activeCategory]);

  /* --- Main render --- */
  return (
    <div className="elderly-care-page">
      {/* Welcome Banner */}
      <section className="ec-welcome-banner">
        <Container>
          <BackButton />
          <div className="ec-welcome-layout">
            <div className="ec-welcome-text">
              <h1 className="ec-welcome-title">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
                {t('elderly:title')}
              </h1>
              <p className="ec-welcome-subtitle">
                {t('elderly:subtitle')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Empty State: Primary account, no elders added — show Add Elder */}
      {membersLoaded && !hasElders && isPrimary && (
        <section style={{ padding: '60px 0' }}>
          <Container>
            <div style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
              borderRadius: 16,
              padding: '48px 24px',
              border: '2px dashed #cbd5e1',
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FontAwesomeIcon icon={faHandHoldingHeart} style={{ fontSize: 36, color: '#0284c7' }} />
              </div>
              <h3 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                No Elders Added Yet
              </h3>
              <p style={{ color: '#64748b', maxWidth: 460, margin: '0 auto 24px', lineHeight: 1.6 }}>
                This section is for tracking the health and well-being of elderly family members (age 60+).
                Add an elder from your family members to get started.
              </p>
              <button
                onClick={() => navigate('/profile')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <FontAwesomeIcon icon={faUserPlus} /> Add Elder in Family Members
              </button>
            </div>
          </Container>
        </section>
      )}

      {/* Empty State: Non-primary (family member) account, not elderly — not eligible */}
      {membersLoaded && !hasElders && !isPrimary && !userIsElderly && (
        <section style={{ padding: '60px 0' }}>
          <Container>
            <div style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              borderRadius: 16,
              padding: '48px 24px',
              border: '1px solid #fecaca',
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FontAwesomeIcon icon={faLock} style={{ fontSize: 36, color: '#dc2626' }} />
              </div>
              <h3 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                Not Eligible
              </h3>
              <p style={{ color: '#64748b', maxWidth: 460, margin: '0 auto 16px', lineHeight: 1.6 }}>
                This section is exclusively for elderly family members aged 60 and above.
                You are currently not eligible to access elderly care services.
              </p>
              <p style={{ color: '#94a3b8', fontSize: 13, maxWidth: 400, margin: '0 auto' }}>
                Please contact your family's primary account holder to get access to elderly care features.
              </p>
            </div>
          </Container>
        </section>
      )}

      {/* Family Member Selector — only when elders exist */}
      {membersLoaded && hasElders && elderlyMembers.length > 1 && (
        <section className="ec-member-selector-section">
          <Container>
            <div className="ec-member-selector">
              <span className="ec-member-label">
                <FontAwesomeIcon icon={faUsers} /> {t('elderly:trackingFor')}
              </span>
              <div className="ec-member-chips">
                {elderlyMembers.map((member) => {
                  const isActive = selectedMember === member.id;
                  return (
                    <button
                      key={member.id ?? 'self'}
                      className={`ec-member-chip${isActive ? ' active' : ''}${member.is_elderly ? ' elderly' : ''}`}
                      onClick={() => setSelectedMember(member.id)}
                    >
                      <span className="ec-member-chip-name">{member.name}</span>
                      <span className="ec-member-chip-rel">{member.relationship}{member.age ? `, ${member.age}y` : ''}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedMember !== null && (
              <div className="ec-member-tracking-banner">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  Showing data for <strong>{elderlyMembers.find(m => m.id === selectedMember)?.name}</strong>.
                  Use the <strong>Record Vital</strong> button to add vitals for this member.
                </span>
              </div>
            )}
          </Container>
        </section>
      )}

      {/* Single elder — show tracking banner */}
      {membersLoaded && hasElders && elderlyMembers.length === 1 && (
        <section className="ec-member-selector-section">
          <Container>
            <div className="ec-member-tracking-banner">
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>
                Showing data for <strong>{elderlyMembers[0]?.name}</strong> ({elderlyMembers[0]?.relationship}{elderlyMembers[0]?.age ? `, ${elderlyMembers[0].age}y` : ''}).
              </span>
            </div>
          </Container>
        </section>
      )}

      {/* Main content — only show when elders exist */}
      {hasElders && (
      <React.Fragment>
      <section className="ec-stats-section">
        <Container>
          <div className="ec-stats-row">
            <div className="ec-stat-card">
              <div className="ec-stat-icon" style={{ background: '#ef444412', color: '#ef4444' }}>
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="ec-stat-info">
                <span className="ec-stat-value">{statCards?.emergency_helpline ?? '--'}</span>
                <span className="ec-stat-label">{t('elderly:stats.emergencyHelpline')}</span>
              </div>
            </div>
            <div className="ec-stat-card" onClick={() => setActiveTab('vitals')} style={{ cursor: 'pointer' }}>
              <div className="ec-stat-icon" style={{ background: '#06b6d412', color: '#06b6d4' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="ec-stat-info">
                <span className="ec-stat-value">{statCards?.vitals_tracked ?? '--'}</span>
                <span className="ec-stat-label">{t('elderly:stats.vitalsTracked')}</span>
              </div>
            </div>
            <div className="ec-stat-card" onClick={() => setActiveTab('reminders')} style={{ cursor: 'pointer' }}>
              <div className="ec-stat-icon" style={{ background: '#f59e0b12', color: '#f59e0b' }}>
                <FontAwesomeIcon icon={faBell} />
              </div>
              <div className="ec-stat-info">
                <span className="ec-stat-value">{statCards?.active_reminders ?? '--'}</span>
                <span className="ec-stat-label">{t('elderly:stats.activeReminders')}</span>
              </div>
            </div>
            <div className="ec-stat-card">
              <div className="ec-stat-icon" style={{ background: '#1976d212', color: '#1976d2' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="ec-stat-info">
                <span className="ec-stat-value">{statCards?.unread_alerts ?? '--'}</span>
                <span className="ec-stat-label">{t('elderly:stats.unreadAlerts')}</span>
              </div>
            </div>
            <div className="ec-stat-card">
              <div className="ec-stat-icon" style={{ background: '#43a04712', color: '#43a047' }}>
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
              <div className="ec-stat-info">
                <span className="ec-stat-value">{statCards?.upcoming_reminders ?? '--'}</span>
                <span className="ec-stat-label">{t('elderly:stats.upcomingReminders')}</span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Tab Navigation */}
      <Container>
        <div className="ec-tabs-wrapper">
          <div className="ec-tabs">
            {dynamicTabs.map((tab) => (
              <button
                key={tab.id}
                className={`ec-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={
                  activeTab === tab.id
                    ? { background: tab.color, borderColor: 'transparent' }
                    : undefined
                }
              >
                <FontAwesomeIcon icon={tab.icon} className="ec-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Tab Content */}
        <section className="ec-tab-content">
          {/* Section header banner */}
          <div
            className="ec-section-header"
            style={{
              background: `linear-gradient(135deg, ${activeTabData.lightColor}, #fff)`,
            }}
          >
            <div className="ec-section-header-text">
              <h2 className="ec-section-title" style={{ color: activeTabData.color }}>
                <FontAwesomeIcon icon={activeTabData.icon} /> {activeDesc.title}
              </h2>
              <p className="ec-section-subtitle">{activeDesc.subtitle}</p>
            </div>
            {activeTabData.image && (
              <div className="ec-section-header-img">
                <img src={activeTabData.image} alt={activeDesc.title} />
              </div>
            )}
          </div>

          {/* Vitals Tab Content */}
          {activeTab === 'vitals' && (
            <>
              {/* Sub-tabs for vitals */}
              <div className="ec-vitals-subtabs">
                <button
                  className={`ec-vitals-subtab${vitalsSubTab === 'overview' ? ' active' : ''}`}
                  onClick={() => setVitalsSubTab('overview')}
                >
                  <FontAwesomeIcon icon={faChartLine} /> {t('elderly:vitals.overview')}
                </button>
                {/* Digital Features sub-tab hidden (duplicates Reminders/other routes) —
                  re-enable via git history.
                <button
                  className={`ec-vitals-subtab${vitalsSubTab === 'digital' ? ' active' : ''}`}
                  onClick={() => setVitalsSubTab('digital')}
                >
                  <FontAwesomeIcon icon={faRobot} /> {t('elderly:vitals.digitalFeatures')}
                </button>
                */}
                {/* Remote Monitoring sub-tab hidden (duplicates Overview; content
                  only redirects elsewhere) — re-enable via git history.
                <button
                  className={`ec-vitals-subtab${vitalsSubTab === 'monitoring' ? ' active' : ''}`}
                  onClick={() => setVitalsSubTab('monitoring')}
                >
                  <FontAwesomeIcon icon={faWifi} /> {t('elderly:vitals.remoteMonitoring')}
                </button>
                */}
                <button
                  className={`ec-vitals-subtab${vitalsSubTab === 'comparison' ? ' active' : ''}`}
                  onClick={() => setVitalsSubTab('comparison')}
                >
                  <FontAwesomeIcon icon={faChartBar} /> {t('elderly:vitals.trends')}
                </button>
              </div>

              {vitalsSubTab === 'overview' && renderVitalsOverview()}
              {/* Digital Features / Remote Monitoring render calls hidden (see comments above)
              {vitalsSubTab === 'digital' && renderDigitalFeatures()}
              {vitalsSubTab === 'monitoring' && renderRemoteMonitoring()}
              */}
              {vitalsSubTab === 'comparison' && renderComparison()}
            </>
          )}

          {/* Reminders Tab Content */}
          {activeTab === 'reminders' && renderReminders()}

          {/* Service cards for remaining tabs */}
          {!['vitals', 'reminders'].includes(activeTab) &&
            renderServiceCards(
              activeServices,
              activeTabData.color,
              activeTabData.lightColor,
            )}
        </section>
      </Container>

      </React.Fragment>
      )}

      {/* In-app toast notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            maxWidth: 480,
            textAlign: 'center',
            background: toastMsg.type === 'success' ? '#10b981' : '#3b82f6',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14, flexShrink: 0 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Enquire Now modal */}
      {enquiryOpen && enquiringService && (
        <div
          className="ec-enquiry-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setEnquiryOpen(false); }}
        >
          <div className="ec-enquiry-modal" style={{ borderTop: `4px solid ${activeTabData.color}` }}>
            <button className="ec-enquiry-close" onClick={() => setEnquiryOpen(false)} title="Close">
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
            <h3 className="ec-enquiry-title">
              <FontAwesomeIcon icon={faComments} style={{ color: activeTabData.color, marginRight: 8 }} />
              {t('elderly:enquiry.title')}
            </h3>
            <p className="ec-enquiry-subtitle">{t('elderly:enquiry.subtitle')}</p>

            <div className="ec-enquiry-grid">
              <div className="ec-form-group ec-enquiry-full">
                <label>{t('elderly:enquiry.serviceLabel')}</label>
                <input
                  type="text"
                  value={enquiringService.title}
                  readOnly
                  style={{ background: '#f1f5f9', cursor: 'default' }}
                />
              </div>

              <div className="ec-form-group ec-enquiry-full">
                <label>{t('elderly:enquiry.forLabel')} <span className="ec-form-required">*</span></label>
                {elderlyMembers.length > 0 ? (
                  <select
                    value={enquiryForm.dependant ?? ''}
                    onChange={(e) =>
                      setEnquiryForm((p) => ({ ...p, dependant: e.target.value ? Number(e.target.value) : null }))
                    }
                  >
                    <option value="">{t('elderly:enquiry.forPlaceholder')}</option>
                    {elderlyMembers.map((m) => (
                      <option key={m.id ?? `m-${m.name}`} value={m.id ?? ''}>
                        {m.name}
                        {m.age ? `, ${m.age}y` : ''}
                        {m.relationship ? ` (${m.relationship})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value=""
                    readOnly
                    placeholder={t('elderly:enquiry.noElders')}
                    style={{ background: '#f1f5f9', cursor: 'default', color: '#94a3b8' }}
                  />
                )}
              </div>

              <div className="ec-form-group">
                <label>{t('elderly:enquiry.name')} <span className="ec-form-required">*</span></label>
                <input
                  type="text"
                  placeholder={t('elderly:enquiry.namePlaceholder')}
                  value={enquiryForm.name}
                  onChange={(e) => setEnquiryForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div className="ec-form-group">
                <label>{t('elderly:enquiry.phone')} <span className="ec-form-required">*</span></label>
                <input
                  type="tel"
                  placeholder={t('elderly:enquiry.phonePlaceholder')}
                  value={enquiryForm.phone}
                  onChange={(e) => setEnquiryForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>

              <div className="ec-form-group">
                <label>{t('elderly:enquiry.email')}</label>
                <input
                  type="email"
                  placeholder={t('elderly:enquiry.emailPlaceholder')}
                  value={enquiryForm.email}
                  onChange={(e) => setEnquiryForm((p) => ({ ...p, email: e.target.value }))}
                />
              </div>

              <div className="ec-form-group">
                <label>{t('elderly:enquiry.preferredTime')}</label>
                <select
                  value={enquiryForm.preferred_time}
                  onChange={(e) => setEnquiryForm((p) => ({ ...p, preferred_time: e.target.value }))}
                >
                  <option value="">{t('elderly:enquiry.preferredPlaceholder')}</option>
                  <option value="any time">{t('elderly:enquiry.preferredAnyTime')}</option>
                  <option value="morning">{t('elderly:enquiry.preferredMorning')}</option>
                  <option value="afternoon">{t('elderly:enquiry.preferredAfternoon')}</option>
                  <option value="evening">{t('elderly:enquiry.preferredEvening')}</option>
                </select>
              </div>

              <div className="ec-form-group ec-enquiry-full">
                <label>{t('elderly:enquiry.message')}</label>
                <textarea
                  rows={2}
                  placeholder={t('elderly:enquiry.messagePlaceholder')}
                  value={enquiryForm.message}
                  onChange={(e) => setEnquiryForm((p) => ({ ...p, message: e.target.value }))}
                />
              </div>
            </div>

            {enquiryError && (
              <div className="ec-enquiry-error">
                <FontAwesomeIcon icon={faExclamationTriangle} /> {enquiryError}
              </div>
            )}

            <div className="ec-form-actions">
              <button className="ec-cancel-btn" onClick={() => setEnquiryOpen(false)}>
                {t('elderly:vitals.cancelRecord')}
              </button>
              <button className="ec-submit-reminder-btn" onClick={submitEnquiry} disabled={enquirySubmitting}>
                <FontAwesomeIcon icon={faCheckCircle} />
                {enquirySubmitting ? t('elderly:enquiry.submitting') : t('elderly:enquiry.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElderlyCare;
